import React, { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import { Search, ChevronDown, ChevronRight, Copy, Check, Menu, X, ExternalLink, Book, Code, Zap, Shield, Settings, Terminal, FileJson, FileCode, Globe, Key, Database, Filter, Clock, Send, Tag, Heart, Keyboard } from "lucide-react";
import Prism from 'prismjs';
import 'prismjs/themes/prism-tomorrow.css';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-kotlin';
import 'prismjs/components/prism-scala';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-xml-doc';
import 'prismjs/components/prism-groovy';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// LocalStorage key for bookmarks
const BOOKMARKS_STORAGE_KEY = 'testing-professor-bookmarks';

// Version definitions for filtering
const VERSIONS = [
  { value: "all", label: "All Versions", color: "#00d4aa" },
  { value: "6.0", label: "6.0+", color: "#00d4aa", minVersion: 6.0 },
  { value: "5.0", label: "5.0+", color: "#3b82f6", minVersion: 5.0 },
  { value: "4.0", label: "4.0+", color: "#8b5cf6", minVersion: 4.0 },
  { value: "3.0", label: "3.0+", color: "#f59e0b", minVersion: 3.0 },
  { value: "2.0", label: "2.0+", color: "#ef4444", minVersion: 2.0 },
];

// Helper to check if feature is available in selected version
const isAvailableInVersion = (featureVersion, selectedVersion) => {
  if (selectedVersion === "all") return true;
  const selected = parseFloat(selectedVersion);
  const feature = parseFloat(featureVersion);
  return feature <= selected;
};

// Get version color
const getVersionColor = (version) => {
  const v = parseFloat(version);
  if (v >= 6.0) return "#00d4aa";
  if (v >= 5.0) return "#3b82f6";
  if (v >= 4.0) return "#8b5cf6";
  if (v >= 3.0) return "#f59e0b";
  return "#ef4444";
};

// REST Assured comprehensive documentation data
const restAssuredData = {
  categories: [
    {
      id: "getting-started",
      title: "Getting Started",
      icon: Zap,
      description: "Essential setup and static imports",
      items: [
        {
          id: "static-imports",
          title: "Static Imports",
          version: "1.0",
          description: "Required imports to use REST Assured effectively",
          code: `import static io.restassured.RestAssured.*;
import static io.restassured.matcher.RestAssuredMatchers.*;
import static org.hamcrest.Matchers.*;

// For JSON Schema validation
import static io.restassured.module.jsv.JsonSchemaValidator.*;

// For Spring MVC testing
import static io.restassured.module.mockmvc.RestAssuredMockMvc.*;`,
          explanation: "These static imports enable the fluent DSL syntax that makes REST Assured so readable. The Hamcrest matchers provide powerful assertion capabilities."
        },
        {
          id: "maven-dependency",
          title: "Maven Dependency",
          version: "1.0",
          description: "Add REST Assured to your project",
          code: `<dependency>
    <groupId>io.rest-assured</groupId>
    <artifactId>rest-assured</artifactId>
    <version>6.0.0</version>
    <scope>test</scope>
</dependency>

<!-- For JSON Schema validation -->
<dependency>
    <groupId>io.rest-assured</groupId>
    <artifactId>json-schema-validator</artifactId>
    <version>6.0.0</version>
</dependency>`,
          explanation: "REST Assured 6.0.0 requires Java 17+, uses Groovy 5, and supports Spring 7 with Jackson 3."
        },
        {
          id: "default-config",
          title: "Default Configuration",
          version: "1.0",
          description: "Configure default values for all requests",
          code: `// Set default base URI and port
RestAssured.baseURI = "http://myhost.org";
RestAssured.port = 8080;
RestAssured.basePath = "/api/v1";

// Set default authentication
RestAssured.authentication = basic("username", "password");

// Set default root path for body assertions
RestAssured.rootPath = "response.data";

// Reset to defaults
RestAssured.reset();`,
          explanation: "Default values apply to all subsequent requests, reducing code duplication. Use reset() to restore original defaults."
        }
      ]
    },
    {
      id: "http-methods",
      title: "HTTP Methods",
      icon: Send,
      description: "GET, POST, PUT, DELETE, PATCH, OPTIONS, HEAD",
      items: [
        {
          id: "get-request",
          version: "1.0",
          title: "GET Request",
          version: "1.0",
          description: "Retrieve data from server",
          code: `// Simple GET
get("/users");

// GET with path parameter
get("/users/{id}", 123);

// GET with query parameters
given()
    .queryParam("status", "active")
    .queryParam("limit", 10)
.when()
    .get("/users")
.then()
    .statusCode(200);

// Full example with validation
given()
    .header("Accept", "application/json")
.when()
    .get("/lotto/{id}", 5)
.then()
    .statusCode(200)
    .body("lotto.lottoId", equalTo(5))
    .body("lotto.winners.winnerId", hasItems(23, 54));`,
          explanation: "GET requests retrieve data without modifying server state. Path parameters use {placeholder} syntax, while query parameters are added via queryParam()."
        },
        {
          id: "post-request",
          version: "1.0",
          title: "POST Request",
          description: "Create new resources",
          code: `// POST with JSON body
given()
    .contentType(ContentType.JSON)
    .body("{ \\"name\\": \\"John\\", \\"email\\": \\"john@example.com\\" }")
.when()
    .post("/users")
.then()
    .statusCode(201)
    .body("id", notNullValue());

// POST with object serialization
User user = new User("John", "john@example.com");
given()
    .contentType(ContentType.JSON)
    .body(user)
.when()
    .post("/users")
.then()
    .statusCode(201);

// POST with form parameters
given()
    .formParam("username", "john")
    .formParam("password", "secret")
.when()
    .post("/login")
.then()
    .statusCode(200);`,
          explanation: "POST creates new resources. The body can be a raw string, Map, or POJO (automatically serialized). Form parameters are used for form submissions."
        },
        {
          id: "put-request",
          version: "1.0",
          title: "PUT Request",
          description: "Update existing resources (full replacement)",
          code: `// PUT to update entire resource
given()
    .contentType(ContentType.JSON)
    .body("{ \\"name\\": \\"John Updated\\", \\"email\\": \\"john.updated@example.com\\" }")
.when()
    .put("/users/{id}", 123)
.then()
    .statusCode(200)
    .body("name", equalTo("John Updated"));

// PUT with object
User updatedUser = new User("John Updated", "john.updated@example.com");
given()
    .contentType(ContentType.JSON)
    .body(updatedUser)
.when()
    .put("/users/{id}", 123)
.then()
    .statusCode(200);`,
          explanation: "PUT replaces the entire resource at the specified URI. Use PATCH for partial updates."
        },
        {
          id: "patch-request",
          version: "2.0",
          title: "PATCH Request",
          description: "Partial update of resources",
          code: `// PATCH for partial update
given()
    .contentType(ContentType.JSON)
    .body("{ \\"email\\": \\"newemail@example.com\\" }")
.when()
    .patch("/users/{id}", 123)
.then()
    .statusCode(200)
    .body("email", equalTo("newemail@example.com"));

// JSON Patch format
given()
    .contentType("application/json-patch+json")
    .body("[{ \\"op\\": \\"replace\\", \\"path\\": \\"/email\\", \\"value\\": \\"new@email.com\\" }]")
.when()
    .patch("/users/{id}", 123)
.then()
    .statusCode(200);`,
          explanation: "PATCH updates only specified fields, unlike PUT which replaces the entire resource."
        },
        {
          id: "delete-request",
          version: "1.0",
          title: "DELETE Request",
          description: "Remove resources from server",
          code: `// Simple DELETE
given()
.when()
    .delete("/users/{id}", 123)
.then()
    .statusCode(204);

// DELETE with authentication
given()
    .auth().basic("admin", "password")
.when()
    .delete("/users/{id}", 123)
.then()
    .statusCode(204);

// DELETE with verification
String userId = "123";
given()
.when()
    .delete("/users/{id}", userId)
.then()
    .statusCode(204);

// Verify deletion
given()
.when()
    .get("/users/{id}", userId)
.then()
    .statusCode(404);`,
          explanation: "DELETE removes resources. 204 No Content is the typical success response. Always verify the resource was actually deleted."
        },
        {
          id: "options-head",
          version: "3.0",
          title: "OPTIONS & HEAD",
          description: "Metadata and capability requests",
          code: `// OPTIONS - Check allowed methods
given()
.when()
    .options("/users")
.then()
    .statusCode(200)
    .header("Allow", containsString("GET"))
    .header("Allow", containsString("POST"));

// HEAD - Get headers without body
given()
.when()
    .head("/users/123")
.then()
    .statusCode(200)
    .header("Content-Type", "application/json")
    .header("Content-Length", notNullValue());

// OPTIONS for CORS preflight
given()
    .header("Origin", "http://example.com")
    .header("Access-Control-Request-Method", "POST")
.when()
    .options("/api/resource")
.then()
    .statusCode(200)
    .header("Access-Control-Allow-Origin", "*");`,
          explanation: "OPTIONS reveals supported HTTP methods and CORS policies. HEAD returns headers identical to GET but without the response body."
        }
      ]
    },
    {
      id: "request-spec",
          version: "1.5",
          title: "Request Specification",
      icon: FileCode,
      description: "Configure request parameters, headers, body, and more",
      items: [
        {
          id: "query-params",
          version: "1.0",
          title: "Query Parameters",
          description: "Add parameters to URL query string",
          code: `// Single parameter
given()
    .queryParam("status", "active")
.when()
    .get("/users");  // GET /users?status=active

// Multiple parameters
given()
    .queryParam("status", "active")
    .queryParam("page", 1)
    .queryParam("limit", 10)
.when()
    .get("/users");  // GET /users?status=active&page=1&limit=10

// Multi-value parameter
given()
    .queryParam("tags", "java", "rest", "api")
.when()
    .get("/posts");  // GET /posts?tags=java&tags=rest&tags=api

// Using Map
Map<String, Object> params = new HashMap<>();
params.put("status", "active");
params.put("page", 1);
given()
    .queryParams(params)
.when()
    .get("/users");

// No-value parameter
given()
    .queryParam("debug")
.when()
    .get("/api");  // GET /api?debug`,
          explanation: "Query parameters are appended to the URL after '?'. Multi-value parameters create multiple key=value pairs."
        },
        {
          id: "path-params",
          version: "1.2",
          title: "Path Parameters",
          description: "Dynamic URL path segments",
          code: `// Inline path parameters
get("/users/{id}", 123);  // GET /users/123

// Named path parameters
given()
    .pathParam("userId", 123)
    .pathParam("orderId", 456)
.when()
    .get("/users/{userId}/orders/{orderId}");  // GET /users/123/orders/456

// Multiple path params with Map
Map<String, Object> pathParams = new HashMap<>();
pathParams.put("userId", 123);
pathParams.put("orderId", 456);
given()
    .pathParams(pathParams)
.when()
    .get("/users/{userId}/orders/{orderId}");

// Combined with query params
given()
    .pathParam("id", 123)
    .queryParam("expand", "orders")
.when()
    .get("/users/{id}");  // GET /users/123?expand=orders`,
          explanation: "Path parameters replace {placeholder} segments in the URL. They're typically used for resource identifiers."
        },
        {
          id: "headers",
          version: "1.0",
          title: "Headers",
          description: "Set HTTP request headers",
          code: `// Single header
given()
    .header("Authorization", "Bearer eyJhbGciOiJIUzI1NiIsInR...")
.when()
    .get("/protected");

// Multiple headers
given()
    .header("Accept", "application/json")
    .header("Accept-Language", "en-US")
    .header("X-Request-ID", "12345")
.when()
    .get("/api");

// Headers with Map
Map<String, String> headers = new HashMap<>();
headers.put("Authorization", "Bearer token");
headers.put("Accept", "application/json");
given()
    .headers(headers)
.when()
    .get("/api");

// Using Headers object
Header authHeader = new Header("Authorization", "Bearer token");
Headers allHeaders = new Headers(authHeader);
given()
    .headers(allHeaders)
.when()
    .get("/api");

// Multi-value header
given()
    .header("Accept", "application/json", "application/xml")
.when()
    .get("/api");`,
          explanation: "Headers convey metadata about the request. Common headers include Authorization, Accept, Content-Type, and custom X- headers."
        },
        {
          id: "cookies",
          version: "1.0",
          title: "Cookies",
          description: "Set HTTP cookies",
          code: `// Single cookie
given()
    .cookie("sessionId", "abc123")
.when()
    .get("/dashboard");

// Multiple cookies
given()
    .cookie("sessionId", "abc123")
    .cookie("userId", "user456")
.when()
    .get("/dashboard");

// Detailed cookie with attributes
given()
    .cookie(new Cookie.Builder("session", "abc123")
        .setPath("/")
        .setDomain("example.com")
        .setMaxAge(3600)
        .setSecured(true)
        .setHttpOnly(true)
        .build())
.when()
    .get("/secure");

// Cookies from Map
Map<String, String> cookies = new HashMap<>();
cookies.put("sessionId", "abc123");
cookies.put("preference", "dark");
given()
    .cookies(cookies)
.when()
    .get("/api");`,
          explanation: "Cookies maintain state between requests. Detailed cookies can specify path, domain, expiration, and security flags."
        },
        {
          id: "request-body",
          version: "1.0",
          title: "Request Body",
          description: "Send data in request body",
          code: `// Raw JSON string
given()
    .contentType(ContentType.JSON)
    .body("{ \\"name\\": \\"John\\", \\"age\\": 30 }")
.when()
    .post("/users");

// Java object (auto-serialized)
User user = new User("John", 30);
given()
    .contentType(ContentType.JSON)
    .body(user)
.when()
    .post("/users");

// Map (auto-serialized to JSON)
Map<String, Object> body = new HashMap<>();
body.put("name", "John");
body.put("age", 30);
given()
    .contentType(ContentType.JSON)
    .body(body)
.when()
    .post("/users");

// XML body
given()
    .contentType(ContentType.XML)
    .body("<user><name>John</name><age>30</age></user>")
.when()
    .post("/users");

// From file
given()
    .contentType(ContentType.JSON)
    .body(new File("src/test/resources/user.json"))
.when()
    .post("/users");`,
          explanation: "The request body carries the payload. REST Assured automatically serializes Java objects based on content type."
        },
        {
          id: "content-type",
          version: "1.0",
          title: "Content Type",
          description: "Specify request content type",
          code: `// Using ContentType enum
given()
    .contentType(ContentType.JSON)
    .body(jsonPayload)
.when()
    .post("/api");

// Using string
given()
    .contentType("application/json")
    .body(jsonPayload)
.when()
    .post("/api");

// Common content types
ContentType.JSON     // application/json
ContentType.XML      // application/xml
ContentType.HTML     // text/html
ContentType.TEXT     // text/plain
ContentType.URLENC   // application/x-www-form-urlencoded
ContentType.BINARY   // application/octet-stream

// With charset
given()
    .contentType("application/json; charset=UTF-8")
    .body(payload)
.when()
    .post("/api");

// Accept header (expected response type)
given()
    .accept(ContentType.JSON)
.when()
    .get("/api");`,
          explanation: "Content-Type tells the server how to interpret the request body. Accept tells the server what response format you want."
        },
        {
          id: "multipart",
          version: "1.3",
          title: "Multi-part Form Data",
          description: "File uploads and form data",
          code: `// Simple file upload
given()
    .multiPart("file", new File("document.pdf"))
.when()
    .post("/upload");

// File with content type
given()
    .multiPart("file", new File("image.png"), "image/png")
.when()
    .post("/upload");

// Multiple files
given()
    .multiPart("files", new File("doc1.pdf"))
    .multiPart("files", new File("doc2.pdf"))
.when()
    .post("/upload");

// File with additional fields
given()
    .multiPart("file", new File("document.pdf"))
    .multiPart("description", "Important document")
    .multiPart("category", "legal")
.when()
    .post("/upload");

// Byte array upload
byte[] fileContent = Files.readAllBytes(Paths.get("file.txt"));
given()
    .multiPart("file", "filename.txt", fileContent)
.when()
    .post("/upload");

// InputStream upload
given()
    .multiPart("file", "data.csv", inputStream, "text/csv")
.when()
    .post("/upload");`,
          explanation: "Multi-part requests are used for file uploads. Each part can be a file, string, or binary data with its own content type."
        }
      ]
    },
    {
      id: "response-validation",
      title: "Response Validation",
      icon: Check,
      description: "Validate status, headers, cookies, and body",
      items: [
        {
          id: "status-code",
          version: "1.0",
          title: "Status Code Validation",
          description: "Verify HTTP response status",
          code: `// Exact status code
get("/users").then().statusCode(200);

// Status line
get("/users").then().statusLine("HTTP/1.1 200 OK");

// Status line with matcher
get("/users").then().statusLine(containsString("OK"));

// Multiple status codes
get("/users").then().statusCode(anyOf(is(200), is(201)));

// Common status code checks
.statusCode(200)  // OK
.statusCode(201)  // Created
.statusCode(204)  // No Content
.statusCode(400)  // Bad Request
.statusCode(401)  // Unauthorized
.statusCode(403)  // Forbidden
.statusCode(404)  // Not Found
.statusCode(500)  // Internal Server Error

// Status code with Hamcrest matchers
get("/users")
.then()
    .statusCode(lessThan(300))
    .statusCode(greaterThanOrEqualTo(200));`,
          explanation: "Status codes indicate request success or failure. 2xx = success, 3xx = redirection, 4xx = client error, 5xx = server error."
        },
        {
          id: "body-validation",
          version: "1.0",
          title: "Body Validation",
          description: "Validate response body content",
          code: `// JSON path validation
get("/lotto")
.then()
    .body("lotto.lottoId", equalTo(5))
    .body("lotto.winners.winnerId", hasItems(23, 54));

// Multiple assertions
get("/user/123")
.then()
    .body("name", equalTo("John"))
    .body("age", greaterThan(18))
    .body("email", containsString("@"));

// Nested JSON validation
get("/order/123")
.then()
    .body("order.items.size()", equalTo(3))
    .body("order.items[0].name", equalTo("Widget"))
    .body("order.total", greaterThan(0.0f));

// Array validation
get("/users")
.then()
    .body("$", hasSize(10))
    .body("[0].name", notNullValue())
    .body("name", hasItem("John"));

// Using root path to reduce duplication
get("/user/123")
.then()
    .root("user.address")
    .body("street", equalTo("123 Main St"))
    .body("city", equalTo("Boston"))
    .body("zip", equalTo("02101"));

// XML validation
get("/greeting")
.then()
    .body("greeting.firstName", equalTo("John"))
    .body("greeting.lastName", equalTo("Doe"));`,
          explanation: "Body validation uses GPath syntax for JSON/XML traversal. The $ or empty string refers to the root of the document."
        },
        {
          id: "header-validation",
          version: "1.0",
          title: "Header Validation",
          description: "Verify response headers",
          code: `// Single header check
get("/api")
.then()
    .header("Content-Type", "application/json");

// Header with matcher
get("/api")
.then()
    .header("Content-Type", containsString("json"))
    .header("Cache-Control", notNullValue());

// Multiple headers
get("/api")
.then()
    .header("Content-Type", "application/json")
    .header("X-Request-Id", notNullValue())
    .header("X-RateLimit-Remaining", greaterThan("0"));

// Header existence
get("/api")
.then()
    .header("Authorization", nullValue())  // Should NOT exist
    .header("Content-Type", notNullValue());  // Should exist

// Custom header validation
get("/api")
.then()
    .header("X-Custom-Header", response -> {
        return response.equals("expected-value");
    });`,
          explanation: "Response headers provide metadata about the response. Common headers include Content-Type, Cache-Control, and custom application headers."
        },
        {
          id: "cookie-validation",
          version: "1.0",
          title: "Cookie Validation",
          description: "Verify response cookies",
          code: `// Simple cookie check
get("/login")
.then()
    .cookie("sessionId", notNullValue());

// Cookie value
get("/login")
.then()
    .cookie("sessionId", "abc123");

// Cookie with matcher
get("/login")
.then()
    .cookie("sessionId", startsWith("sess_"));

// Multiple cookies
get("/login")
.then()
    .cookie("sessionId", notNullValue())
    .cookie("userId", notNullValue())
    .cookie("preference", equalTo("dark"));

// Detailed cookie attributes
get("/login")
.then()
    .cookie("sessionId")  // Just check existence
    .detailedCookie("sessionId", 
        cookie -> cookie.getPath().equals("/") && 
                  cookie.isHttpOnly());`,
          explanation: "Cookies set by the server can be validated for value, path, domain, expiration, and security attributes."
        },
        {
          id: "content-type-validation",
          version: "1.0",
          title: "Content Type Validation",
          description: "Verify response content type",
          code: `// Exact content type
get("/api")
.then()
    .contentType(ContentType.JSON);

// Content type as string
get("/api")
.then()
    .contentType("application/json");

// Content type with matcher
get("/api")
.then()
    .contentType(containsString("json"));

// Ignore charset in comparison
get("/api")
.then()
    .contentType(ContentType.JSON);  // Matches "application/json; charset=utf-8"

// Combined with body validation
get("/users")
.then()
    .contentType(ContentType.JSON)
    .body("$", hasSize(greaterThan(0)));`,
          explanation: "Content-Type validation ensures the server returns data in the expected format."
        },
        {
          id: "time-validation",
          version: "2.5",
          title: "Response Time",
          description: "Validate response performance",
          code: `// Response time less than threshold
get("/api")
.then()
    .time(lessThan(2000L));  // Less than 2 seconds

// Response time in specific unit
get("/api")
.then()
    .time(lessThan(500L), TimeUnit.MILLISECONDS);

// Response time with matcher
get("/api")
.then()
    .time(lessThanOrEqualTo(1000L));

// Extract response time
long responseTime = get("/api").time();
long responseTimeMs = get("/api").timeIn(TimeUnit.MILLISECONDS);

// Performance assertion
get("/api")
.then()
    .time(allOf(
        greaterThanOrEqualTo(100L),
        lessThan(1000L)
    ));`,
          explanation: "Response time validation helps ensure API performance meets requirements. Times are in milliseconds by default."
        },
        {
          id: "schema-validation",
          version: "1.1",
          title: "JSON Schema Validation",
          description: "Validate response against JSON schema",
          code: `// Schema from classpath
get("/products")
.then()
    .body(matchesJsonSchemaInClasspath("product-schema.json"));

// Schema from file
get("/products")
.then()
    .body(matchesJsonSchema(new File("schemas/product.json")));

// Schema from string
String schema = "{ \\"type\\": \\"object\\", \\"properties\\": { \\"id\\": { \\"type\\": \\"integer\\" } } }";
get("/products")
.then()
    .body(matchesJsonSchema(schema));

// With custom schema factory
JsonSchemaFactory factory = JsonSchemaFactory.newBuilder()
    .setValidationConfiguration(
        ValidationConfiguration.newBuilder()
            .setDefaultVersion(DRAFTV4)
            .freeze())
    .freeze();

get("/products")
.then()
    .body(matchesJsonSchemaInClasspath("schema.json")
        .using(factory));

// Unchecked validation
get("/products")
.then()
    .body(matchesJsonSchemaInClasspath("schema.json")
        .using(settings().with().checkedValidation(false)));`,
          explanation: "JSON Schema validation ensures the response structure matches a predefined contract, catching API changes early."
        },
        {
          id: "xml-validation",
          version: "1.1",
          title: "XML Schema & DTD Validation",
          description: "Validate XML responses",
          code: `// XSD validation
get("/cars")
.then()
    .body(matchesXsd(new File("car-schema.xsd")));

// XSD from classpath
get("/cars")
.then()
    .body(matchesXsdInClasspath("schemas/car-schema.xsd"));

// DTD validation
get("/videos")
.then()
    .body(matchesDtd(new File("video.dtd")));

// DTD from classpath
get("/videos")
.then()
    .body(matchesDtdInClasspath("schemas/video.dtd"));

// XPath validation
get("/greeting")
.then()
    .body(hasXPath("/greeting/firstName", containsString("Jo")))
    .body(hasXPath("/greeting/firstName[text()='John']"));

// XML namespace handling
given()
    .config(config().xmlConfig(
        xmlConfig().declareNamespace("ns", "http://example.com")))
.when()
    .get("/namespaced")
.then()
    .body("foo.ns:bar.text()", equalTo("value"));`,
          explanation: "XML responses can be validated against XSD schemas or DTDs. XPath expressions provide powerful query capabilities."
        }
      ]
    },
    {
      id: "json-xml-path",
      title: "JsonPath & XmlPath",
      icon: FileJson,
      description: "Extract and query response data",
      items: [
        {
          id: "jsonpath-basics",
          version: "1.0",
          title: "JsonPath Basics",
          description: "Extract values from JSON responses",
          code: `// Get response as JsonPath
JsonPath json = get("/user/123").jsonPath();

// Extract single value
String name = json.getString("name");
int age = json.getInt("age");
float price = json.getFloat("product.price");
boolean active = json.getBoolean("status.active");

// Extract object
Map<String, Object> address = json.getMap("address");
User user = json.getObject("", User.class);

// Extract list
List<String> names = json.getList("users.name");
List<Integer> ids = json.getList("users.id", Integer.class);
List<User> users = json.getList("users", User.class);

// Direct extraction from response
String name = get("/user/123").path("name");
List<String> tags = get("/post/1").path("tags");

// Using from() static import
String response = get("/user").asString();
String name = from(response).getString("name");`,
          explanation: "JsonPath uses GPath syntax (Groovy's path expression language) to navigate JSON structures. Paths are dot-separated."
        },
        {
          id: "jsonpath-advanced",
          version: "1.0",
          title: "Advanced JsonPath",
          description: "Complex queries with closures and filters",
          code: `// Find with closure
List<String> expensiveBooks = from(response)
    .getList("store.book.findAll { it.price > 10 }.title");

// Find single item
String cheapestBook = from(response)
    .getString("store.book.find { it.price == store.book.price.min() }.title");

// Collect transformed values
List<Integer> titleLengths = from(response)
    .getList("store.book.title.collect { it.length() }");

// Sum values
double totalPrice = from(response)
    .getDouble("store.book.price.sum()");

// Min/Max
double maxPrice = from(response)
    .getDouble("store.book.price.max()");

// Size
int bookCount = from(response)
    .getInt("store.book.size()");

// Spread operator for method calls
int totalAuthorLength = from(response)
    .getInt("store.book.author*.length().sum()");

// Multiple conditions
List<String> titles = from(response)
    .getList("store.book.findAll { it.price < 10 && it.category == 'fiction' }.title");

// Depth-first search with **
String groceryItem = from(response)
    .getString("**.find { it.@type == 'groceries' }.item[0]");`,
          explanation: "GPath supports Groovy closures for powerful filtering, mapping, and aggregation operations on JSON data."
        },
        {
          id: "xmlpath-basics",
          version: "1.0",
          title: "XmlPath Basics",
          description: "Extract values from XML responses",
          code: `// Get response as XmlPath
XmlPath xml = get("/order/123").xmlPath();

// Extract values
String customerName = xml.getString("order.customer.name");
int quantity = xml.getInt("order.items.item[0].quantity");

// Extract list
List<String> itemNames = xml.getList("order.items.item.name");

// Extract with namespace
XmlPath xml = given()
    .config(config().xmlConfig(
        xmlConfig().declareNamespace("ns", "http://example.com")))
    .get("/data").xmlPath();
String value = xml.getString("root.ns:element");

// Using from() static import
String response = get("/order").asString();
String name = from(response).getString("order.customer.name");

// Get attributes
String type = xml.getString("order.@type");
String id = xml.getString("order.items.item[0].@id");

// Get text content
String text = xml.getString("order.notes.text()");`,
          explanation: "XmlPath uses similar GPath syntax adapted for XML. Attributes are accessed with @ prefix, text content with text()."
        },
        {
          id: "xmlpath-groovy",
          version: "1.0",
          title: "XmlPath with Groovy Closures",
          description: "Complex XML queries",
          code: `// Find elements with closure
List<String> expensiveItems = from(xmlResponse)
    .getList("order.items.item.findAll { it.price.toFloat() > 100 }.name");

// Find single element
String firstExpensive = from(xmlResponse)
    .getString("order.items.item.find { it.price.toFloat() > 100 }.name");

// Depth-first search
String groceryCategory = from(xmlResponse)
    .getString("**.find { it.@type == 'groceries' }");

// Collect attribute values
List<String> allIds = from(xmlResponse)
    .getList("order.items.item.@id");

// Filter by attribute
List<String> activeItems = from(xmlResponse)
    .getList("order.items.item.findAll { it.@status == 'active' }.name");

// Nested element access
String streetAddress = from(xmlResponse)
    .getString("order.customer.address.street");

// Handle CDATA
String description = from(xmlResponse)
    .getString("product.description");  // CDATA content extracted automatically`,
          explanation: "XmlPath supports the same powerful Groovy closures as JsonPath for filtering and transforming XML data."
        }
      ]
    },
    {
      id: "authentication",
      title: "Authentication",
      icon: Key,
      description: "Basic, Digest, OAuth, Form, and custom auth",
      items: [
        {
          id: "basic-auth",
          version: "1.0",
          title: "Basic Authentication",
          description: "Username/password in Authorization header",
          code: `// Challenged basic auth (waits for 401)
given()
    .auth().basic("username", "password")
.when()
    .get("/secured");

// Preemptive basic auth (sends credentials immediately)
given()
    .auth().preemptive().basic("username", "password")
.when()
    .get("/secured");

// Set default authentication
RestAssured.authentication = basic("username", "password");

// Preemptive as default
RestAssured.authentication = preemptive().basic("username", "password");

// In request specification
RequestSpecification spec = new RequestSpecBuilder()
    .setAuth(preemptive().basic("user", "pass"))
    .build();

given()
    .spec(spec)
.when()
    .get("/secured");`,
          explanation: "Basic auth encodes credentials in Base64. Preemptive sends credentials without waiting for a 401 challenge, improving performance."
        },
        {
          id: "digest-auth",
          version: "1.0",
          title: "Digest Authentication",
          description: "More secure challenge-response authentication",
          code: `// Digest authentication
given()
    .auth().digest("username", "password")
.when()
    .get("/digest-secured");

// Set as default
RestAssured.authentication = digest("username", "password");

// In request specification
RequestSpecification spec = new RequestSpecBuilder()
    .setAuth(digest("user", "pass"))
    .build();`,
          explanation: "Digest auth is more secure than Basic as it doesn't send the password in clear text. It uses a challenge-response mechanism with MD5 hashing."
        },
        {
          id: "oauth",
          version: "1.5",
          title: "OAuth 1.0 & 2.0",
          description: "Token-based authentication",
          code: `// OAuth 1.0
given()
    .auth().oauth(
        "consumerKey", 
        "consumerSecret", 
        "accessToken", 
        "secretToken")
.when()
    .get("/oauth1-secured");

// OAuth 2.0 with access token
given()
    .auth().oauth2("accessToken")
.when()
    .get("/oauth2-secured");

// OAuth 2.0 with custom header name
given()
    .auth().oauth2("accessToken", OAuthSignature.HEADER)
.when()
    .get("/oauth2-secured");

// OAuth 2.0 as query parameter
given()
    .auth().oauth2("accessToken", OAuthSignature.QUERY_STRING)
.when()
    .get("/oauth2-secured");  // ?access_token=...

// Set OAuth2 as default
RestAssured.authentication = oauth2("accessToken");

// Bearer token (equivalent to OAuth2)
given()
    .header("Authorization", "Bearer " + accessToken)
.when()
    .get("/secured");`,
          explanation: "OAuth provides delegated authorization. OAuth 2.0 is simpler, using just an access token. OAuth 1.0 requires signing requests."
        },
        {
          id: "form-auth",
          version: "1.2",
          title: "Form Authentication",
          description: "Login via HTML form submission",
          code: `// Basic form authentication
given()
    .auth().form("username", "password")
.when()
    .get("/protected");

// Form auth with custom field names
given()
    .auth().form("username", "password",
        new FormAuthConfig("/login", "user", "pass"))
.when()
    .get("/protected");

// Form auth with additional fields
given()
    .auth().form("username", "password",
        formAuthConfig()
            .withLoginPageUrl("/login")
            .withUsernameField("email")
            .withPasswordField("pwd")
            .withAdditionalField("remember", "true"))
.when()
    .get("/protected");

// Form auth with CSRF token
given()
    .auth().form("username", "password",
        formAuthConfig()
            .withCsrfFieldName("_csrf")
            .withAutoDetectCsrfFieldName())
.when()
    .post("/login");`,
          explanation: "Form authentication simulates a user logging in through an HTML form. REST Assured handles the session automatically."
        },
        {
          id: "custom-auth",
          version: "1.5",
          title: "Custom Authentication",
          description: "Implement custom authentication schemes",
          code: `// Using header directly
given()
    .header("X-API-Key", "your-api-key")
.when()
    .get("/api/data");

// Using filter for custom auth
given()
    .filter((requestSpec, responseSpec, ctx) -> {
        String timestamp = String.valueOf(System.currentTimeMillis());
        String signature = calculateHMAC(requestSpec, timestamp);
        requestSpec.header("X-Timestamp", timestamp);
        requestSpec.header("X-Signature", signature);
        return ctx.next(requestSpec, responseSpec);
    })
.when()
    .get("/api/data");

// Reusable auth filter
public class HmacAuthFilter implements Filter {
    @Override
    public Response filter(FilterableRequestSpecification requestSpec,
                          FilterableResponseSpecification responseSpec,
                          FilterContext ctx) {
        String signature = calculateSignature(requestSpec);
        requestSpec.header("Authorization", "HMAC " + signature);
        return ctx.next(requestSpec, responseSpec);
    }
}

// Use custom filter
given()
    .filter(new HmacAuthFilter())
.when()
    .get("/api/secure");`,
          explanation: "Custom authentication can be implemented using headers directly or via filters for more complex schemes like HMAC signing."
        }
      ]
    },
    {
      id: "specifications",
      title: "Specifications",
      icon: Database,
      description: "Reusable request and response specifications",
      items: [
        {
          id: "request-spec",
          version: "1.5",
          title: "Request Specification",
          description: "Reusable request configuration",
          code: `// Build request specification
RequestSpecification requestSpec = new RequestSpecBuilder()
    .setBaseUri("https://api.example.com")
    .setPort(443)
    .setBasePath("/v1")
    .addHeader("Accept", "application/json")
    .addHeader("Authorization", "Bearer token")
    .setContentType(ContentType.JSON)
    .build();

// Use in test
given()
    .spec(requestSpec)
    .body(payload)
.when()
    .post("/users")
.then()
    .statusCode(201);

// Extend specification
given()
    .spec(requestSpec)
    .header("X-Custom", "value")  // Add to existing headers
    .queryParam("debug", true)
.when()
    .get("/debug");

// Set as default for all requests
RestAssured.requestSpecification = requestSpec;

// Query specification values
QueryableRequestSpecification queryable = SpecificationQuerier.query(requestSpec);
String authHeader = queryable.getHeaders().getValue("Authorization");
String contentType = queryable.getContentType();`,
          explanation: "Request specifications encapsulate common request settings. They can be extended per-test or set as defaults for all tests."
        },
        {
          id: "response-spec",
          version: "1.5",
          title: "Response Specification",
          description: "Reusable response expectations",
          code: `// Build response specification
ResponseSpecification responseSpec = new ResponseSpecBuilder()
    .expectStatusCode(200)
    .expectContentType(ContentType.JSON)
    .expectHeader("X-Request-Id", notNullValue())
    .expectBody("status", equalTo("success"))
    .expectResponseTime(lessThan(2000L))
    .build();

// Use in test
given()
    .body(payload)
.when()
    .post("/api/action")
.then()
    .spec(responseSpec)
    .body("data.id", notNullValue());  // Additional assertions

// Set as default for all responses
RestAssured.responseSpecification = responseSpec;

// Different specs for different scenarios
ResponseSpecification successSpec = new ResponseSpecBuilder()
    .expectStatusCode(200)
    .expectBody("success", equalTo(true))
    .build();

ResponseSpecification errorSpec = new ResponseSpecBuilder()
    .expectStatusCode(400)
    .expectBody("error", notNullValue())
    .build();`,
          explanation: "Response specifications define expected response characteristics. Test-specific assertions are merged with the specification."
        },
        {
          id: "spec-inheritance",
          version: "1.5",
          title: "Specification Inheritance",
          description: "Building specifications from others",
          code: `// Base specification
RequestSpecBuilder baseBuilder = new RequestSpecBuilder()
    .setBaseUri("https://api.example.com")
    .setContentType(ContentType.JSON)
    .addHeader("Accept", "application/json");

// Auth specification extending base
RequestSpecification authSpec = new RequestSpecBuilder()
    .addRequestSpecification(baseBuilder.build())
    .addHeader("Authorization", "Bearer token")
    .build();

// Admin specification extending auth
RequestSpecification adminSpec = new RequestSpecBuilder()
    .addRequestSpecification(authSpec)
    .addHeader("X-Admin", "true")
    .build();

// Response spec inheritance
ResponseSpecBuilder baseResponseBuilder = new ResponseSpecBuilder()
    .expectContentType(ContentType.JSON)
    .expectResponseTime(lessThan(5000L));

ResponseSpecification successResponse = new ResponseSpecBuilder()
    .addResponseSpecification(baseResponseBuilder.build())
    .expectStatusCode(200)
    .build();`,
          explanation: "Specifications can inherit from other specifications, enabling a hierarchy of reusable configurations."
        }
      ]
    },
    {
      id: "filters-logging",
      title: "Filters & Logging",
      icon: Filter,
      description: "Request/response interception and logging",
      items: [
        {
          id: "request-logging",
          version: "1.5",
          title: "Request Logging",
          description: "Log request details before sending",
          code: `// Log everything
given()
    .log().all()
.when()
    .get("/api");

// Log specific parts
given().log().params().when().get("/api");     // Parameters only
given().log().body().when().post("/api");       // Body only
given().log().headers().when().get("/api");     // Headers only
given().log().cookies().when().get("/api");     // Cookies only
given().log().method().when().get("/api");      // HTTP method only
given().log().path().when().get("/api");        // Request path only
given().log().uri().when().get("/api");         // Full URI

// Log if validation fails
given()
    .log().ifValidationFails()
.when()
    .get("/api")
.then()
    .statusCode(200);

// Configure logging globally
RestAssured.enableLoggingOfRequestAndResponseIfValidationFails();

// Log to custom print stream
PrintStream fileStream = new PrintStream("request.log");
given()
    .filter(new RequestLoggingFilter(fileStream))
.when()
    .get("/api");`,
          explanation: "Request logging helps debug test failures by showing exactly what was sent to the server."
        },
        {
          id: "response-logging",
          version: "1.5",
          title: "Response Logging",
          description: "Log response details after receiving",
          code: `// Log everything
get("/api").then().log().all();

// Log specific parts
get("/api").then().log().body();        // Body only
get("/api").then().log().headers();     // Headers only
get("/api").then().log().cookies();     // Cookies only
get("/api").then().log().status();      // Status line only
get("/api").then().log().statusLine();  // Same as status()

// Conditional logging
get("/api").then().log().ifError();                    // Log on 4xx/5xx
get("/api").then().log().ifStatusCodeIsEqualTo(500);   // Log on specific status
get("/api").then().log().ifStatusCodeMatches(greaterThan(400)); // Log with matcher

// Log if validation fails
get("/api")
.then()
    .log().ifValidationFails()
    .statusCode(200);

// Log to custom stream
get("/api")
.then()
    .filter(new ResponseLoggingFilter(LogDetail.ALL, fileStream))
    .statusCode(200);`,
          explanation: "Response logging shows what the server returned. Conditional logging reduces noise by only logging failures."
        },
        {
          id: "blacklist-headers",
          version: "4.2",
          title: "Blacklist Headers",
          description: "Hide sensitive headers in logs",
          code: `// Blacklist single header
given()
    .config(config().logConfig(
        logConfig().blacklistHeader("Authorization")))
    .log().all()
.when()
    .get("/api");
// Output: Authorization=[ BLACKLISTED ]

// Blacklist multiple headers
given()
    .config(config().logConfig(
        logConfig()
            .blacklistHeader("Authorization")
            .blacklistHeader("X-API-Key")
            .blacklistHeader("Cookie")))
    .log().all()
.when()
    .get("/api");

// Set globally
RestAssured.config = config().logConfig(
    logConfig()
        .blacklistHeader("Authorization")
        .blacklistHeader("X-Secret"));`,
          explanation: "Blacklisting prevents sensitive data like API keys and tokens from appearing in logs."
        },
        {
          id: "custom-filters",
          version: "1.5",
          title: "Custom Filters",
          description: "Intercept and modify requests/responses",
          code: `// Simple logging filter
Filter loggingFilter = (requestSpec, responseSpec, ctx) -> {
    System.out.println("Request: " + requestSpec.getURI());
    Response response = ctx.next(requestSpec, responseSpec);
    System.out.println("Response: " + response.getStatusCode());
    return response;
};

given()
    .filter(loggingFilter)
.when()
    .get("/api");

// Request modification filter
Filter authFilter = (requestSpec, responseSpec, ctx) -> {
    String token = getAuthToken();  // Dynamic token retrieval
    requestSpec.header("Authorization", "Bearer " + token);
    return ctx.next(requestSpec, responseSpec);
};

// Response modification filter
Filter responseFilter = (requestSpec, responseSpec, ctx) -> {
    Response originalResponse = ctx.next(requestSpec, responseSpec);
    // Modify response
    return new ResponseBuilder()
        .clone(originalResponse)
        .setBody(modifiedBody)
        .build();
};

// Ordered filter (controls execution order)
public class MyOrderedFilter implements OrderedFilter {
    @Override
    public int getOrder() {
        return 100;  // Lower = higher priority
    }
    
    @Override
    public Response filter(FilterableRequestSpecification requestSpec,
                          FilterableResponseSpecification responseSpec,
                          FilterContext ctx) {
        // Filter logic
        return ctx.next(requestSpec, responseSpec);
    }
}

// Multiple filters
given()
    .filter(authFilter)
    .filter(loggingFilter)
    .filter(new ErrorLoggingFilter())
.when()
    .get("/api");`,
          explanation: "Filters provide AOP-style interception for requests and responses. They're useful for cross-cutting concerns like auth, logging, and metrics."
        },
        {
          id: "builtin-filters",
          version: "1.5",
          title: "Built-in Filters",
          description: "REST Assured's provided filters",
          code: `// Request logging filter
given()
    .filter(new RequestLoggingFilter())
.when()
    .get("/api");

// Response logging filter (log on specific status)
given()
    .filter(new ResponseLoggingFilter(200))
.when()
    .get("/api");

// Error logging filter (logs 4xx/5xx responses)
given()
    .filter(new ErrorLoggingFilter())
.when()
    .get("/api");

// Session filter (maintains session across requests)
SessionFilter sessionFilter = new SessionFilter();

given()
    .filter(sessionFilter)
    .auth().form("user", "pass")
.when()
    .post("/login");

// Session is automatically applied
given()
    .filter(sessionFilter)
.when()
    .get("/dashboard");

// Get session ID from filter
String sessionId = sessionFilter.getSessionId();

// Add filters globally
RestAssured.filters(
    new RequestLoggingFilter(),
    new ResponseLoggingFilter(),
    new ErrorLoggingFilter()
);`,
          explanation: "Built-in filters handle common scenarios. SessionFilter is particularly useful for maintaining login state across requests."
        }
      ]
    },
    {
      id: "serialization",
      title: "Serialization",
      icon: Code,
      description: "Object mapping and custom serialization",
      items: [
        {
          id: "json-serialization",
          version: "1.4",
          title: "JSON Serialization",
          description: "Convert objects to JSON",
          code: `// POJO to JSON (automatic with Jackson/Gson)
User user = new User("John", 30, "john@example.com");
given()
    .contentType(ContentType.JSON)
    .body(user)  // Automatically serialized
.when()
    .post("/users");

// Map to JSON
Map<String, Object> body = new HashMap<>();
body.put("name", "John");
body.put("age", 30);
given()
    .contentType(ContentType.JSON)
    .body(body)
.when()
    .post("/users");

// Explicit Jackson serialization
given()
    .contentType(ContentType.JSON)
    .body(user, ObjectMapperType.JACKSON_2)
.when()
    .post("/users");

// Explicit Gson serialization
given()
    .contentType(ContentType.JSON)
    .body(user, ObjectMapperType.GSON)
.when()
    .post("/users");

// Custom ObjectMapper
ObjectMapper mapper = new ObjectMapper();
mapper.setSerializationInclusion(Include.NON_NULL);

given()
    .config(config().objectMapperConfig(
        objectMapperConfig().jackson2ObjectMapperFactory(
            (cls, charset) -> mapper)))
    .body(user)
.when()
    .post("/users");`,
          explanation: "REST Assured auto-detects serialization library (Jackson, Gson, Johnzon) based on classpath. Content-Type determines format."
        },
        {
          id: "json-deserialization",
          version: "1.4",
          title: "JSON Deserialization",
          description: "Convert JSON responses to objects",
          code: `// Deserialize to POJO
User user = get("/users/123").as(User.class);

// Deserialize with explicit type
User user = get("/users/123").as(User.class, ObjectMapperType.JACKSON_2);

// Deserialize list with TypeRef
List<User> users = get("/users")
    .as(new TypeRef<List<User>>() {});

// Deserialize nested generic
Response<List<User>> response = get("/users")
    .as(new TypeRef<Response<List<User>>>() {});

// Extract and deserialize in one step
User user = given()
    .pathParam("id", 123)
.when()
    .get("/users/{id}")
.then()
    .statusCode(200)
    .extract()
    .as(User.class);

// Deserialize part of response
Address address = get("/users/123")
    .jsonPath()
    .getObject("address", Address.class);

// Deserialize list from path
List<Order> orders = get("/users/123")
    .jsonPath()
    .getList("orders", Order.class);`,
          explanation: "Deserialization converts JSON responses back to Java objects. TypeRef handles generic types like List<T>."
        },
        {
          id: "xml-serialization",
          version: "1.4",
          title: "XML Serialization",
          description: "Convert objects to/from XML",
          code: `// POJO to XML (requires JAXB annotations)
@XmlRootElement
public class User {
    private String name;
    private int age;
    // getters, setters
}

given()
    .contentType(ContentType.XML)
    .body(user)  // Serialized to XML via JAXB
.when()
    .post("/users");

// Deserialize XML to POJO
User user = get("/users/123")
    .as(User.class);  // Content-Type determines format

// Explicit JAXB
given()
    .contentType(ContentType.XML)
    .body(user, ObjectMapperType.JAXB)
.when()
    .post("/users");

// Extract XML values
String name = get("/users/123")
    .xmlPath()
    .getString("user.name");

// Deserialize from XML path
Address address = get("/users/123")
    .xmlPath()
    .getObject("user.address", Address.class);`,
          explanation: "XML serialization uses JAXB by default. Classes need @XmlRootElement and other JAXB annotations."
        },
        {
          id: "custom-serialization",
          version: "1.4",
          title: "Custom Serialization",
          description: "Implement custom object mappers",
          code: `// Custom ObjectMapper implementation
ObjectMapper customMapper = new ObjectMapper() {
    @Override
    public Object deserialize(ObjectMapperDeserializationContext ctx) {
        // Custom deserialization logic
        return parseMyWay(ctx.getDataToDeserialize().asString());
    }

    @Override
    public Object serialize(ObjectMapperSerializationContext ctx) {
        // Custom serialization logic
        return serializeMyWay(ctx.getObjectToSerialize());
    }
};

// Register custom mapper for content type
given()
    .config(config().objectMapperConfig(
        objectMapperConfig()
            .defaultObjectMapper(customMapper)))
    .body(myObject)
.when()
    .post("/api");

// Custom content-type deserializer
RestAssured.config = config().objectMapperConfig(
    objectMapperConfig()
        .defaultObjectMapperType(ObjectMapperType.JACKSON_2));

// Configure Jackson with modules
ObjectMapper jacksonMapper = new ObjectMapper();
jacksonMapper.registerModule(new JavaTimeModule());
jacksonMapper.disable(SerializationFeature.WRITE_DATES_AS_TIMESTAMPS);

given()
    .config(config().objectMapperConfig(
        objectMapperConfig().jackson2ObjectMapperFactory(
            (cls, charset) -> jacksonMapper)))
.when()
    .get("/api");`,
          explanation: "Custom serialization allows handling special formats or configuring serialization behavior precisely."
        }
      ]
    },
    {
      id: "configuration",
      title: "Configuration",
      icon: Settings,
      description: "Advanced REST Assured configuration options",
      items: [
        {
          id: "ssl-config",
          version: "1.2",
          title: "SSL Configuration",
          description: "Configure HTTPS and certificates",
          code: `// Relaxed HTTPS validation (trust all certs)
given()
    .relaxedHTTPSValidation()
.when()
    .get("https://self-signed.example.com");

// Relaxed validation with specific protocol
given()
    .relaxedHTTPSValidation("TLS")
.when()
    .get("https://secure.example.com");

// Set globally
RestAssured.useRelaxedHTTPSValidation();

// Custom keystore
given()
    .keyStore("/path/to/keystore.jks", "password")
.when()
    .get("https://api.example.com");

// Trust store
KeyStore trustStore = loadTrustStore();
RestAssured.trustStore(trustStore);

// SSL Config for fine-grained control
RestAssured.config = config().sslConfig(
    sslConfig()
        .keystoreType("PKCS12")
        .keyStore("/path/to/cert.p12", "password")
        .trustStore("/path/to/truststore.jks", "password")
        .allowAllHostnames());

// Allow all hostnames
RestAssured.config = config().sslConfig(
    sslConfig().allowAllHostnames());`,
          explanation: "SSL configuration handles certificate validation. Use relaxedHTTPSValidation() for testing with self-signed certs."
        },
        {
          id: "proxy-config",
          version: "2.3",
          title: "Proxy Configuration",
          description: "Route requests through a proxy",
          code: `// Simple proxy
given()
    .proxy("proxy.example.com", 8080)
.when()
    .get("/api");

// Localhost proxy (common for debugging)
given()
    .proxy(8080)  // Assumes localhost
.when()
    .get("/api");

// HTTPS proxy
given()
    .proxy(host("proxy.example.com").withScheme("https"))
.when()
    .get("/api");

// Authenticated proxy
given()
    .proxy(auth("proxyUser", "proxyPass"))
.when()
    .get("/api");

// Combined host and auth
given()
    .proxy(host("proxy.example.com")
        .withPort(8080)
        .withAuth("user", "pass"))
.when()
    .get("/api");

// Set proxy globally
RestAssured.proxy("proxy.example.com", 8080);

// Or using ProxySpecification
RestAssured.proxy = host("proxy.example.com").withPort(8080);

// In request specification
RequestSpecification spec = new RequestSpecBuilder()
    .setProxy("proxy.example.com")
    .build();`,
          explanation: "Proxy configuration routes traffic through an intermediary, useful for debugging with tools like Charles or Fiddler."
        },
        {
          id: "encoder-decoder",
          version: "1.5",
          title: "Encoder/Decoder Config",
          description: "Configure character encoding",
          code: `// Default content charset
RestAssured.config = config().encoderConfig(
    encoderConfig().defaultContentCharset("UTF-8"));

// Default query parameter charset
RestAssured.config = config().encoderConfig(
    encoderConfig().defaultQueryParameterCharset("UTF-8"));

// Charset for specific content type
RestAssured.config = config().encoderConfig(
    encoderConfig()
        .defaultCharsetForContentType("UTF-16", "application/xml"));

// Don't append charset to content-type
RestAssured.config = config().encoderConfig(
    encoderConfig()
        .appendDefaultContentCharsetToContentTypeIfUndefined(false));

// Decoder config for response
RestAssured.config = config().decoderConfig(
    decoderConfig().defaultContentCharset("UTF-8"));

// Content decoders (GZIP, DEFLATE)
RestAssured.config = config().decoderConfig(
    decoderConfig().contentDecoders(GZIP, DEFLATE));

// Disable GZIP, keep DEFLATE
RestAssured.config = config().decoderConfig(
    decoderConfig().contentDecoders(DEFLATE));`,
          explanation: "Encoder/Decoder configs handle character set encoding for requests and responses. JSON defaults to UTF-8 per RFC."
        },
        {
          id: "redirect-config",
          version: "1.5",
          title: "Redirect Configuration",
          description: "Control redirect behavior",
          code: `// Disable redirects
given()
    .redirects().follow(false)
.when()
    .get("/redirect");

// Limit max redirects
given()
    .redirects().max(5)
.when()
    .get("/redirect");

// Allow circular redirects
given()
    .config(config().redirect(
        redirectConfig().followRedirects(true)
            .allowCircularRedirects(true)))
.when()
    .get("/redirect");

// Reject relative redirects
given()
    .config(config().redirect(
        redirectConfig().rejectRelativeRedirect(true)))
.when()
    .get("/redirect");

// Global redirect settings
RestAssured.config = config().redirect(
    redirectConfig()
        .followRedirects(false)
        .maxRedirects(10));

// Using DSL
given()
    .redirects().follow(true).and().redirects().max(3)
.when()
    .get("/redirect");`,
          explanation: "Redirect configuration controls how REST Assured handles HTTP 3xx redirect responses."
        },
        {
          id: "param-config",
          version: "2.3",
          title: "Parameter Configuration",
          description: "Control parameter handling",
          code: `// Merge parameters (default)
given()
    .queryParam("key", "value1")
    .queryParam("key", "value2")  // Results in key=value1&key=value2
.when()
    .get("/api");

// Replace parameters instead of merge
given()
    .config(config().paramConfig(
        paramConfig().queryParamsUpdateStrategy(REPLACE)))
    .queryParam("key", "value1")
    .queryParam("key", "value2")  // Results in key=value2
.when()
    .get("/api");

// Replace all parameter types
given()
    .config(config().paramConfig(
        paramConfig().replaceAllParameters()))
.when()
    .get("/api");

// Different strategies per param type
RestAssured.config = config().paramConfig(
    paramConfig()
        .queryParamsUpdateStrategy(REPLACE)
        .formParamsUpdateStrategy(MERGE));`,
          explanation: "Parameter config controls whether duplicate parameters are merged or replaced. Default is merge."
        },
        {
          id: "http-client-config",
          version: "1.5",
          title: "HTTP Client Configuration",
          description: "Configure underlying HTTP client",
          code: `// Reuse HTTP client instance
RestAssured.config = config().httpClient(
    httpClientConfig().reuseHttpClientInstance());

// Custom HTTP client factory
RestAssured.config = config().httpClient(
    httpClientConfig().httpClientFactory(
        () -> HttpClients.custom()
            .setConnectionManager(connectionManager)
            .build()));

// Set HTTP client parameters
RestAssured.config = config().httpClient(
    httpClientConfig()
        .setParam("http.socket.timeout", 60000)
        .setParam("http.connection.timeout", 60000));

// Connection config
RestAssured.config = config().connectionConfig(
    connectionConfig()
        .closeIdleConnectionsAfterEachResponse());

// Timeouts via request config
given()
    .config(config().httpClient(
        httpClientConfig()
            .setParam("http.connection.timeout", 5000)
            .setParam("http.socket.timeout", 5000)))
.when()
    .get("/slow-api");`,
          explanation: "HTTP client config provides low-level control over connection pooling, timeouts, and other HTTP client behavior."
        },
        {
          id: "failure-config",
          version: "3.3",
          title: "Failure Configuration",
          description: "Handle validation failures",
          code: `// Add failure listener
ResponseValidationFailureListener listener = (reqSpec, respSpec, resp) -> {
    // Log to file, send alert, etc.
    System.err.println("Test failed! Status: " + resp.getStatusCode());
    System.err.println("Response: " + resp.getBody().asString());
};

given()
    .config(config().failureConfig(
        failureConfig().failureListeners(listener)))
.when()
    .get("/api")
.then()
    .statusCode(200);

// Multiple listeners
given()
    .config(config().failureConfig(
        failureConfig().failureListeners(
            logListener,
            emailListener,
            metricsListener)))
.when()
    .get("/api");

// Custom failure message
get("/api")
.then()
    .onFailMessage("Custom error message for debugging")
    .statusCode(200);

// Global failure config
RestAssured.config = config().failureConfig(
    failureConfig().failureListeners(globalListener));`,
          explanation: "Failure config allows custom handling when assertions fail - useful for logging, alerting, or custom reporting."
        }
      ]
    },
    {
      id: "advanced",
      title: "Advanced Features",
      icon: Terminal,
      description: "CSRF, sessions, root path, and more",
      items: [
        {
          id: "csrf-support",
          version: "5.2",
          title: "CSRF Support",
          description: "Handle Cross-Site Request Forgery tokens",
          code: `// CSRF with form token
given()
    .csrf("/login-page")  // Page to fetch CSRF token from
.when()
    .post("/login");

// Custom CSRF field name
given()
    .csrf("/login-page", "_csrf")
.when()
    .post("/login");

// CSRF via config (more control)
given()
    .config(config().csrfConfig(
        csrfConfig()
            .csrfTokenPath("/csrf-page")
            .csrfInputFieldName("_token")))
.when()
    .post("/api");

// CSRF with logging
given()
    .config(config().csrfConfig(
        csrfConfig()
            .csrfTokenPath("/login")
            .loggingEnabled(LogDetail.ALL)))
.when()
    .post("/login");

// Header-based CSRF token
given()
    .config(config().csrfConfig(
        csrfConfig()
            .csrfTokenPath("/api/csrf")
            .csrfHeaderName("X-CSRF-TOKEN")))
.when()
    .post("/api/action");`,
          explanation: "CSRF support automatically fetches and includes CSRF tokens from a specified page, handling anti-CSRF protection."
        },
        {
          id: "session-support",
          version: "1.2",
          title: "Session Management",
          description: "Maintain session state across requests",
          code: `// Set session ID
given()
    .sessionId("abc123")
.when()
    .get("/dashboard");

// Custom session ID name
RestAssured.config = config().sessionConfig(
    sessionConfig().sessionIdName("PHPSESSID"));

// Session filter (automatic session management)
SessionFilter session = new SessionFilter();

// Login and capture session
given()
    .filter(session)
    .formParam("user", "john")
    .formParam("pass", "secret")
.when()
    .post("/login");

// Subsequent requests use captured session
given()
    .filter(session)
.when()
    .get("/protected-page")
.then()
    .statusCode(200);

// Get captured session ID
String sessionId = session.getSessionId();

// Default session ID for all requests
RestAssured.sessionId = "default-session-id";

// Extract session from response
String sid = get("/login").sessionId();`,
          explanation: "Session support simplifies testing authenticated flows by maintaining session state automatically."
        },
        {
          id: "root-path",
          version: "1.1",
          title: "Root Path",
          description: "Reduce path duplication in assertions",
          code: `// Without root path (repetitive)
get("/user/123")
.then()
    .body("user.profile.name", equalTo("John"))
    .body("user.profile.email", equalTo("john@example.com"))
    .body("user.profile.age", greaterThan(18));

// With root path
get("/user/123")
.then()
    .root("user.profile")
    .body("name", equalTo("John"))
    .body("email", equalTo("john@example.com"))
    .body("age", greaterThan(18));

// Append to root
get("/store")
.then()
    .root("store.book[0]")
    .body("title", equalTo("Book Title"))
    .appendRoot("author")
    .body("name", equalTo("Author Name"));

// Detach from root
get("/store")
.then()
    .root("store.category")
    .body("size()", equalTo(4))
    .detachRoot("category")
    .body("size()", equalTo(1));

// Path arguments in root
get("/store")
.then()
    .root("store.%s", withArgs("book"))
    .body("category.size()", equalTo(4))
    .appendRoot("%s.%s", withArgs("author", "size()"))
    .body(withNoArgs(), equalTo(4));

// Global root path
RestAssured.rootPath = "response.data";`,
          explanation: "Root path eliminates repetitive path prefixes in body assertions, making tests cleaner and more maintainable."
        },
        {
          id: "response-extraction",
          version: "1.5",
          title: "Response Extraction",
          description: "Extract data from responses",
          code: `// Extract entire response
Response response = get("/users/123");
String body = response.getBody().asString();
int statusCode = response.getStatusCode();
String contentType = response.getContentType();

// Extract after validation
String name = given()
.when()
    .get("/users/123")
.then()
    .statusCode(200)
    .extract()
    .path("name");

// Extract multiple values
ExtractableResponse<Response> extract = given()
.when()
    .get("/users/123")
.then()
    .statusCode(200)
    .extract();

String name = extract.path("name");
String email = extract.path("email");
Response response = extract.response();

// Extract response object
User user = get("/users/123")
.then()
    .extract()
    .as(User.class);

// Extract headers
String authHeader = get("/api")
.then()
    .extract()
    .header("Authorization");

Headers allHeaders = get("/api")
.then()
    .extract()
    .headers();

// Extract cookies
String sessionCookie = get("/login")
.then()
    .extract()
    .cookie("JSESSIONID");

Map<String, String> allCookies = get("/login")
.then()
    .extract()
    .cookies();`,
          explanation: "Response extraction allows capturing response data for use in subsequent requests or assertions."
        },
        {
          id: "url-encoding",
          version: "1.2",
          title: "URL Encoding",
          description: "Control URL encoding behavior",
          code: `// URL encoding enabled (default)
given()
    .queryParam("search", "hello world")  // Encoded as hello%20world
.when()
    .get("/search");

// Disable URL encoding (for pre-encoded params)
given()
    .urlEncodingEnabled(false)
    .queryParam("jql", "project%20%3D%20BAM")  // Already encoded
.when()
    .get("/search");

// Global setting
RestAssured.urlEncodingEnabled = false;

// Per-request with pre-encoded URL
String encodedQuery = URLEncoder.encode("status=active", "UTF-8");
given()
    .urlEncodingEnabled(false)
.when()
    .get("/api?" + encodedQuery);

// Path param encoding
given()
    .pathParam("name", "John Doe")  // Encoded in path
.when()
    .get("/users/{name}");`,
          explanation: "URL encoding converts special characters for safe URL transmission. Disable it when parameters are pre-encoded."
        },
        {
          id: "groovy-closures",
          version: "1.0",
          title: "Groovy Closures in Validation",
          description: "Use Groovy power in assertions",
          code: `// Find in list
get("/store")
.then()
    .body("store.book.find { it.price < 10 }.title", 
          equalTo("Sayings of the Century"));

// Find all matching
get("/store")
.then()
    .body("store.book.findAll { it.price < 10 }.title",
          hasItems("Sayings of the Century", "Moby Dick"));

// Collect/map transformation
get("/store")
.then()
    .body("store.book.collect { it.price }.sum()", 
          greaterThan(40.0f));

// Max/min values
get("/store")
.then()
    .body("store.book.price.max()", equalTo(22.99f))
    .body("store.book.price.min()", equalTo(8.95f));

// Size/count
get("/users")
.then()
    .body("$.size()", greaterThan(0))
    .body("findAll { it.active == true }.size()", equalTo(5));

// Spread operator
get("/store")
.then()
    .body("store.book.author*.length().sum()", greaterThan(50));

// Depth-first search with **
get("/shopping")
.then()
    .body("**.find { it.@type == 'groceries' }.item",
          hasItems("Chocolate", "Coffee"));

// Complex condition
get("/users")
.then()
    .body("findAll { it.age > 18 && it.status == 'active' }.size()",
          greaterThan(0));`,
          explanation: "REST Assured leverages Groovy's GPath and closures for powerful, expressive JSON/XML queries and validations."
        }
      ]
    },
    {
      id: "hamcrest",
      title: "Hamcrest Matchers",
      icon: Globe,
      description: "Common matchers for assertions",
      items: [
        {
          id: "equality-matchers",
          version: "1.0",
          title: "Equality Matchers",
          description: "Check for equal values",
          code: `// Equal to
.body("name", equalTo("John"))

// Equal ignoring case
.body("status", equalToIgnoringCase("ACTIVE"))

// Same instance
.body("singleton", sameInstance(expectedInstance))

// Is (alias for equalTo)
.body("count", is(10))

// Not
.body("status", not(equalTo("INACTIVE")))
.body("error", not("error message"))

// Null checks
.body("optional", nullValue())
.body("required", notNullValue())

// Instance of
.body("data", instanceOf(Map.class))

// String equality
.body("message", equalToCompressingWhiteSpace("hello  world"))`,
          explanation: "Equality matchers verify exact values. is() is syntactic sugar for equalTo(). Use not() to negate any matcher."
        },
        {
          id: "string-matchers",
          version: "1.0",
          title: "String Matchers",
          description: "String content validation",
          code: `// Contains substring
.body("message", containsString("success"))

// Starts with
.body("id", startsWith("usr_"))

// Ends with
.body("filename", endsWith(".pdf"))

// Contains in order
.body("log", stringContainsInOrder("start", "process", "end"))

// Empty/blank string
.body("description", emptyString())
.body("whitespace", blankString())
.body("notes", emptyOrNullString())

// String length
.body("code", hasLength(6))

// Matches regex
.body("email", matchesRegex("[a-z]+@[a-z]+\\\\.[a-z]+"))
.body("phone", matchesPattern("\\\\d{3}-\\\\d{3}-\\\\d{4}"))

// Case insensitive contains
.body("status", containsStringIgnoringCase("SUCCESS"))`,
          explanation: "String matchers provide various ways to validate text content, from simple contains to regex matching."
        },
        {
          id: "number-matchers",
          version: "1.0",
          title: "Number Matchers",
          description: "Numeric comparisons",
          code: `// Greater than
.body("age", greaterThan(18))
.body("price", greaterThanOrEqualTo(0.0f))

// Less than
.body("stock", lessThan(100))
.body("discount", lessThanOrEqualTo(50))

// Close to (with delta)
.body("pi", closeTo(3.14, 0.01))

// Comparisons combined
.body("score", allOf(
    greaterThanOrEqualTo(0),
    lessThanOrEqualTo(100)))

// In range
.body("percentage", both(
    greaterThanOrEqualTo(0.0f))
    .and(lessThanOrEqualTo(100.0f)))`,
          explanation: "Number matchers handle numeric comparisons. Use closeTo() for floating-point comparisons with tolerance."
        },
        {
          id: "collection-matchers",
          version: "1.0",
          title: "Collection Matchers",
          description: "Array and list validation",
          code: `// Has item(s)
.body("tags", hasItem("java"))
.body("tags", hasItems("java", "rest", "api"))

// Contains exactly (order matters)
.body("steps", contains("init", "process", "complete"))

// Contains in any order
.body("roles", containsInAnyOrder("admin", "user", "guest"))

// Size
.body("items", hasSize(5))
.body("results", hasSize(greaterThan(0)))

// Empty
.body("errors", empty())
.body("warnings", emptyIterable())

// Every item matches
.body("prices", everyItem(greaterThan(0.0f)))
.body("names", everyItem(not(emptyString())))

// Has entry (for maps)
.body("config", hasEntry("debug", true))
.body("headers", hasKey("Content-Type"))
.body("response", hasValue("success"))

// Array at index
.body("items[0]", equalTo("first"))
.body("users.name", hasItem("John"))`,
          explanation: "Collection matchers validate arrays, lists, and maps. contains() checks exact order, hasItems() doesn't."
        },
        {
          id: "combining-matchers",
          version: "1.0",
          title: "Combining Matchers",
          description: "Logical combinations of matchers",
          code: `// All of (AND)
.body("age", allOf(
    greaterThan(0),
    lessThan(150),
    notNullValue()))

// Any of (OR)
.body("status", anyOf(
    equalTo("active"),
    equalTo("pending"),
    equalTo("processing")))

// Both/and
.body("score", both(greaterThan(0)).and(lessThan(100)))

// Either/or
.body("type", either(equalTo("A")).or(equalTo("B")))

// Not
.body("error", not(containsString("fatal")))
.body("status", is(not(equalTo("error"))))

// Complex combination
.body("user", allOf(
    hasEntry("name", notNullValue()),
    hasEntry("email", containsString("@")),
    hasEntry(equalTo("active"), equalTo(true))))

// Nested matchers
.body("items", hasItem(allOf(
    hasEntry("name", startsWith("Product")),
    hasEntry("price", greaterThan(0.0f)))))`,
          explanation: "Combining matchers creates complex assertions. allOf() requires all to pass, anyOf() requires at least one."
        }
      ]
    },
    {
      id: "spring-mockmvc",
      title: "Spring MockMvc",
      icon: Shield,
      description: "Unit testing Spring MVC Controllers",
      items: [
        {
          id: "mockmvc-setup",
          version: "2.2",
          title: "MockMvc Setup",
          description: "Bootstrap RestAssuredMockMvc",
          code: `// Maven dependency
<dependency>
    <groupId>io.rest-assured</groupId>
    <artifactId>spring-mock-mvc</artifactId>
    <version>6.0.0</version>
    <scope>test</scope>
</dependency>

// Static imports
import static io.restassured.module.mockmvc.RestAssuredMockMvc.*;
import static io.restassured.module.mockmvc.matcher.RestAssuredMockMvcMatchers.*;

// Standalone setup with controller
given().
    standaloneSetup(new GreetingController()).
    param("name", "Johan").
when().
    get("/greeting").
then().
    statusCode(200).
    body("content", equalTo("Hello, Johan!"));

// Static setup (applies to all tests)
RestAssuredMockMvc.standaloneSetup(new GreetingController());

// WebApplicationContext setup
@Autowired
private WebApplicationContext context;

RestAssuredMockMvc.webAppContextSetup(context);`,
          explanation: "RestAssuredMockMvc allows unit testing Spring Controllers without starting a server. Use standaloneSetup for isolated tests or webAppContextSetup for full context."
        },
        {
          id: "mockmvc-async",
          version: "2.4",
          title: "Async Requests",
          description: "Testing asynchronous Spring controllers",
          code: `// Controller returning Callable
@RequestMapping(value = "/async", method = POST)
public @ResponseBody Callable<String> asyncEndpoint(@RequestBody String body) {
    return () -> body;
}

// Test async endpoint with default timeout (1 second)
given().
    body("async request").
when().
    async().post("/async").
then().
    body(equalTo("async request"));

// Custom timeout
given().
    body("a string").
when().
    async().with().timeout(20, TimeUnit.SECONDS).post("/async").
then().
    body(equalTo("a string"));

// Global async config
RestAssuredMockMvc.config = RestAssuredMockMvc.config()
    .asyncConfig(withTimeout(100, TimeUnit.MILLISECONDS));`,
          explanation: "Async support handles Spring's Callable/DeferredResult controllers. Configure timeouts per-request or globally via AsyncConfig."
        },
        {
          id: "mockmvc-postprocessors",
          version: "2.3",
          title: "Request Post Processors",
          description: "Modify requests before execution",
          code: `// Add custom post processor
given().
    postProcessors(myPostProcessor1, myPostProcessor2).
when().
    get("/endpoint");

// Spring Security post processors
import static org.springframework.security.test.web.servlet.request.SecurityMockMvcRequestPostProcessors.*;

// HTTP Basic auth
given().
    auth().with(httpBasic("username", "password")).
when().
    get("/secured");

// CSRF token
given().
    postProcessors(csrf()).
when().
    post("/protected");

// User authentication
given().
    postProcessors(user("admin").roles("ADMIN")).
when().
    get("/admin");`,
          explanation: "Post processors modify MockHttpServletRequest before execution. Spring Security provides processors for authentication, CSRF, and user injection."
        },
        {
          id: "mockmvc-result-handlers",
          version: "2.3",
          title: "Result Handlers & Matchers",
          description: "Spring MockMvc result handling",
          code: `// Result handlers (e.g., logging)
import static org.springframework.test.web.servlet.result.MockMvcResultHandlers.*;

given().
    param("name", "Johan").
when().
    get("/greeting").
then().
    apply(print());  // Print request/response to console

// Result matchers
import static org.springframework.test.web.servlet.result.MockMvcResultMatchers.*;

given().
    param("name", "Johan").
when().
    get("/greeting").
then().
    assertThat(status().isOk()).
    assertThat(content().contentType("application/json")).
    assertThat(jsonPath("$.name").value("Johan")).
    body("id", equalTo(1));

// Security result matchers
import static org.springframework.security.test.web.servlet.response.SecurityMockMvcResultMatchers.*;

given().
    auth().with(httpBasic("user", "pass")).
when().
    get("/secured").
then().
    expect(authenticated().withUsername("user"));`,
          explanation: "Result handlers perform actions after request (like logging). Result matchers use Spring's assertion API alongside REST Assured's body assertions."
        },
        {
          id: "mockmvc-interceptors",
          version: "2.4",
          title: "Interceptors & Specifications",
          description: "Advanced MockMvc customization",
          code: `// Custom interceptor
MockHttpServletRequestBuilderInterceptor interceptor = 
    (requestBuilder, context) -> {
        requestBuilder.header("X-Custom", "value");
    };

given().
    interceptor(interceptor).
when().
    get("/api");

// MockMvc Request Specification
MockMvcRequestSpecification spec = new MockMvcRequestSpecBuilder()
    .addQueryParam("name", "Johan")
    .setContentType(ContentType.JSON)
    .build();

given().
    spec(spec).
when().
    get("/greeting");

// Static specifications
RestAssuredMockMvc.requestSpecification = new MockMvcRequestSpecBuilder()
    .addHeader("Accept", "application/json")
    .build();

RestAssuredMockMvc.responseSpecification = new ResponseSpecBuilder()
    .expectStatusCode(200)
    .build();

// Reset MockMvc state
RestAssuredMockMvc.reset();`,
          explanation: "Interceptors modify MockHttpServletRequestBuilder directly. MockMvcRequestSpecBuilder creates reusable request configurations."
        },
        {
          id: "mockmvc-security",
          version: "2.4",
          title: "Spring Security Testing",
          description: "Test secured endpoints with Spring Security",
          code: `// Setup with Spring Security
@Autowired
private WebApplicationContext context;

@Before
public void setup() {
    RestAssuredMockMvc.webAppContextSetup(context);
}

// HTTP Basic authentication
given().
    auth().with(httpBasic("user", "password")).
when().
    get("/secured").
then().
    statusCode(200);

// Static authentication for all requests
RestAssuredMockMvc.authentication = with(httpBasic("user", "pass"));

// Using @WithMockUser annotation
@WithMockUser(username = "admin", roles = {"ADMIN"})
@Test
public void adminEndpointTest() {
    given().
        webAppContextSetup(context).
    when().
        get("/admin").
    then().
        statusCode(200);
}

// Using @WithUserDetails
@WithUserDetails("customUser")
@Test
public void customUserTest() {
    given().
        webAppContextSetup(context).
    when().
        get("/user-aware").
    then().
        body(equalTo("Success"));
}`,
          explanation: "Spring Security Test annotations (@WithMockUser, @WithUserDetails) inject authenticated users. Use httpBasic() or other SecurityMockMvcRequestPostProcessors for auth."
        }
      ]
    },
    {
      id: "spring-webtestclient",
      title: "Spring WebTestClient",
      icon: Globe,
      description: "Testing reactive Spring WebFlux Controllers",
      items: [
        {
          id: "webtestclient-setup",
          version: "3.2",
          title: "WebTestClient Setup",
          description: "Bootstrap RestAssuredWebTestClient for reactive testing",
          code: `// Maven dependency
<dependency>
    <groupId>io.rest-assured</groupId>
    <artifactId>spring-web-test-client</artifactId>
    <version>6.0.0</version>
    <scope>test</scope>
</dependency>

// Static imports
import static io.restassured.module.webtestclient.RestAssuredWebTestClient.*;
import static io.restassured.module.webtestclient.matcher.RestAssuredWebTestClientMatchers.*;

// Standalone setup with reactive controller
given().
    standaloneSetup(new ReactiveGreetingController()).
    param("name", "Johan").
when().
    get("/greeting").
then().
    statusCode(200).
    body("content", equalTo("Hello, Johan!"));

// Static setup
RestAssuredWebTestClient.standaloneSetup(new ReactiveGreetingController());

// WebTestClient instance setup
WebTestClient webTestClient = WebTestClient
    .bindToController(new GreetingController())
    .build();
    
RestAssuredWebTestClient.webTestClient(webTestClient);`,
          explanation: "RestAssuredWebTestClient tests Spring WebFlux reactive controllers. It uses WebTestClient internally but provides REST Assured's fluent API."
        },
        {
          id: "webtestclient-reactive",
          version: "3.2",
          title: "Testing Reactive Endpoints",
          description: "Test Mono and Flux returning controllers",
          code: `// Reactive Controller returning Mono
@RestController
public class ReactiveController {
    @GetMapping(value = "/mono", produces = "application/json")
    public Mono<Greeting> mono(@RequestParam String name) {
        return Mono.just(new Greeting(1, "Hello, " + name + "!"));
    }
    
    @GetMapping(value = "/flux", produces = "application/json")
    public Flux<Greeting> flux() {
        return Flux.just(
            new Greeting(1, "Hello"),
            new Greeting(2, "World")
        );
    }
}

// Test Mono endpoint
given().
    standaloneSetup(new ReactiveController()).
    param("name", "Johan").
when().
    get("/mono").
then().
    statusCode(200).
    body("id", equalTo(1)).
    body("content", equalTo("Hello, Johan!"));

// Test Flux endpoint
given().
    standaloneSetup(new ReactiveController()).
when().
    get("/flux").
then().
    statusCode(200).
    body("$", hasSize(2)).
    body("[0].content", equalTo("Hello")).
    body("[1].content", equalTo("World"));`,
          explanation: "WebTestClient handles Mono (single value) and Flux (multiple values) reactive types seamlessly. Flux responses are collected into arrays."
        },
        {
          id: "webtestclient-specs",
          version: "3.2",
          title: "WebTestClient Specifications",
          description: "Reusable configurations for reactive tests",
          code: `// Request specification
WebTestClientRequestSpecification spec = new WebTestClientRequestSpecBuilder()
    .addQueryParam("name", "Johan")
    .addHeader("Accept", "application/json")
    .setContentType(ContentType.JSON)
    .build();

given().
    spec(spec).
when().
    get("/greeting").
then().
    statusCode(200);

// Static specifications
RestAssuredWebTestClient.requestSpecification = new WebTestClientRequestSpecBuilder()
    .addHeader("Authorization", "Bearer token")
    .build();

RestAssuredWebTestClient.responseSpecification = new ResponseSpecBuilder()
    .expectStatusCode(200)
    .expectContentType(ContentType.JSON)
    .build();

// Reset to defaults
RestAssuredWebTestClient.reset();

// With WebApplicationContext
@Autowired
private ApplicationContext context;

given().
    applicationContextSetup(context).
when().
    get("/api").
then().
    statusCode(200);`,
          explanation: "WebTestClientRequestSpecBuilder creates reusable request specs. Use applicationContextSetup for full Spring context including security."
        }
      ]
    },
    {
      id: "kotlin-extensions",
      title: "Kotlin Extensions",
      icon: Code,
      description: "Kotlin-friendly DSL for REST Assured",
      items: [
        {
          id: "kotlin-basic",
          version: "4.1",
          title: "Kotlin Basic Usage",
          description: "Using REST Assured in Kotlin",
          code: `// Problem: 'when' is a reserved keyword in Kotlin
// Option 1: Escape with backticks
@Test
fun \`kotlin rest assured example\`() {
    given().
        param("firstName", "Johan").
    \`when\`().  // Escaped
        get("/greeting").
    then().
        statusCode(200).
        body("greeting.firstName", equalTo("Johan"))
}

// Option 2: Create extension function
fun RequestSpecification.When(): RequestSpecification {
    return this.\`when\`()
}

// Now use without escaping
@Test
fun kotlinExample() {
    given().
        param("firstName", "Johan").
    When().  // No escaping needed
        get("/greeting").
    then().
        statusCode(200)
}`,
          explanation: "Kotlin's 'when' keyword conflicts with REST Assured's when(). Use backtick escaping or create a When() extension function."
        },
        {
          id: "kotlin-extensions-module",
          version: "4.1",
          title: "Kotlin Extensions Module",
          description: "Native Kotlin DSL for REST Assured",
          code: `// Maven dependency
<dependency>
    <groupId>io.rest-assured</groupId>
    <artifactId>kotlin-extensions</artifactId>
    <version>6.0.0</version>
    <scope>test</scope>
</dependency>

// Import
import io.restassured.module.kotlin.extensions.*

// Kotlin DSL syntax
val message: String =
Given {
    port(7000)
    header("Header", "HeaderValue")
    body("request body")
} When {
    put("/the/path")
} Then {
    statusCode(200)
    body("message", equalTo("Success"))
} Extract {
    path("message")
}

// Benefits:
// 1. All failed expectations reported at once
// 2. IDE formatting doesn't break indentation
// 3. More idiomatic Kotlin code`,
          explanation: "The kotlin-extensions module provides Given/When/Then/Extract DSL that's more Kotlin-friendly and reports all assertion failures together."
        },
        {
          id: "kotlin-spring-mockmvc",
          version: "4.2",
          title: "Kotlin Spring MockMvc Extensions",
          description: "Kotlin DSL for Spring MockMvc testing",
          code: `// Maven dependency
<dependency>
    <groupId>io.rest-assured</groupId>
    <artifactId>spring-mock-mvc-kotlin-extensions</artifactId>
    <version>6.0.0</version>
    <scope>test</scope>
</dependency>

// Import
import io.restassured.module.mockmvc.kotlin.extensions.*

// Test Spring MVC controller with Kotlin DSL
class GreetingControllerTest {
    @Test
    fun \`greeting controller test\`() {
        val mockMvc = MockMvcBuilders
            .standaloneSetup(GreetingController())
            .build()

        val id: Int =
        Given {
            mockMvc(mockMvc)
            param("name", "Johan")
        } When {
            get("/greeting")
        } Then {
            body(
                "id", Matchers.equalTo(1),
                "content", Matchers.equalTo("Hello, Johan!")
            )
        } Extract {
            path("id")
        }

        assertThat(id).isEqualTo(1)
    }
}`,
          explanation: "spring-mock-mvc-kotlin-extensions provides the same Kotlin DSL for MockMvc-based testing of Spring MVC controllers."
        },
        {
          id: "kotlin-spring-webtest",
          version: "5.4",
          title: "Kotlin Spring WebTest Extensions",
          description: "Kotlin DSL for Spring WebFlux testing",
          code: `// Maven dependency
<dependency>
    <groupId>io.rest-assured</groupId>
    <artifactId>spring-web-test-client-kotlin-extensions</artifactId>
    <version>6.0.0</version>
    <scope>test</scope>
</dependency>

// Import
import io.restassured.module.webtestclient.kotlin.extensions.*

// Test reactive controller with Kotlin DSL
class ReactiveControllerTest {
    private val webTestClient = WebTestClient
        .bindToController(GreetingController())
        .build()

    @Test
    fun \`reactive greeting test\`() {
        val id: Int =
        Given {
            webTestClient(webTestClient)
            param("name", "Johan")
        } When {
            get("/greeting")
        } Then {
            body(
                "id", Matchers.equalTo(1),
                "content", Matchers.equalTo("Hello, Johan!")
            )
        } Extract {
            path("id")
        }

        assertThat(id).isEqualTo(1)
    }
}`,
          explanation: "spring-web-test-client-kotlin-extensions provides the Kotlin DSL for testing reactive Spring WebFlux controllers."
        }
      ]
    },
    {
      id: "scala-support",
      title: "Scala Support",
      icon: Terminal,
      description: "Using REST Assured with Scala",
      items: [
        {
          id: "scala-extension-module",
          version: "5.5",
          title: "Scala Extension Module",
          description: "Scala-friendly API for REST Assured",
          code: `// Maven/SBT dependency
<dependency>
    <groupId>io.rest-assured</groupId>
    <artifactId>scala-support</artifactId>
    <version>6.0.0</version>
    <scope>test</scope>
</dependency>

// Import
import io.restassured.module.scala.RestAssuredSupport.AddThenToResponse

// Scala syntax with implicit conversion
class ScalaRestAssuredTest extends AnyFunSuite {
  test("Scala REST Assured example") {
    given().
      param("firstName", "Johan").
      param("lastName", "Haleby").
    when().
      get("/greeting").
    Then().
      statusCode(200).
      body("greeting.firstName", equalTo("Johan")).
      body("greeting.lastName", equalTo("Haleby"))
  }
}

// The Then() method is added via implicit conversion
// to avoid conflict with Scala's test frameworks`,
          explanation: "The scala-support module adds a Then() method to avoid conflicts with ScalaTest's then keyword. Import AddThenToResponse for the implicit conversion."
        }
      ]
    },
    {
      id: "version-history",
      title: "Version History",
      icon: Clock,
      description: "REST Assured evolution and migration guides",
      items: [
        {
          id: "version-6",
          version: "1.0",
          title: "Version 6.0.0 (Dec 2025)",
          description: "Java 17+, Groovy 5, Spring 7 support",
          code: `// REST Assured 6.0.0 - Major Release
// Release Date: December 12, 2025

// Key Requirements:
// - Java 17+ (previously Java 8)
// - Groovy 5.x (previously 4.x)
// - Spring 5.3+ minimum (previously 5.1)

// New Features:
// - Spring 7 support for MockMvc and WebTestClient
// - Jackson 3 support
// - Yasson 3 support (minimum required)
// - Johnzon 2 support (minimum required)
// - Memory leak fix in JsonPath (migrated fully to Java)

// Maven Dependency
<dependency>
    <groupId>io.rest-assured</groupId>
    <artifactId>rest-assured</artifactId>
    <version>6.0.0</version>
    <scope>test</scope>
</dependency>

// Breaking Changes:
// - Minimum Java version is now 17
// - Minimum Spring version is now 5.3+`,
          explanation: "Version 6.0.0 modernizes REST Assured with Java 17 baseline, Groovy 5, and support for latest Spring/Jackson versions. Major memory leak fix for heavy JsonPath usage."
        },
        {
          id: "version-5x",
          version: "1.0",
          title: "Version 5.x Series (2022-2025)",
          description: "CSRF support, Kotlin extensions, reactive testing",
          code: `// REST Assured 5.5.x - Latest 5.x Series
// Key Features Added:

// 5.5.0 - Scala 3 Extensions
import io.restassured.module.scala.extensions.*
Given(req => req.port(7000).body("hello"))
  .When(_.put("/path"))
  .Extract(_.path("message"))

// 5.4.0 - WebTestClient Kotlin Extensions
import io.restassured.module.webtestclient.kotlin.extensions.*
Given { webTestClient(client) }
When { get("/greeting") }
Then { body("id", equalTo(1)) }

// 5.3.x - Improved CSRF with Cookies
given().
    csrf("/login").  // Auto-apply cookies from CSRF fetch
    formParam("name", "value").
when().
    post("/users");

// 5.2.0 - Major CSRF Overhaul
given().
    csrf("/page").  // Fetch CSRF token automatically
    formParam("data", "value").
when().
    post("/submit");

// 5.1.0 - Spring 6 / Jakarta EE Support
// MockMvc module supports Spring Framework 6.0

// 5.0.0 - Groovy 4 Upgrade
// Breaking: Uses Groovy 4.x internally

// Migration from 4.x to 5.x:
// - No API changes required
// - Groovy 4 may affect custom closures`,
          explanation: "The 5.x series added major features: improved CSRF, Kotlin extensions, Scala 3 support, and Spring 6/Jakarta EE compatibility."
        },
        {
          id: "version-4x",
          version: "1.0",
          title: "Version 4.x Series (2019-2022)",
          description: "Java 8+, Hamcrest 2, Kotlin module",
          code: `// REST Assured 4.x - Java 8 Era
// Key Requirements:
// - Java 8+ (previously Java 6)
// - Hamcrest 2.1 (previously 1.3)
// - Groovy 2.5.x

// 4.5.0 - Jakarta EE 8/9 Support
// Use Jakarta XML Binding instead of javax.xml.bind
<dependency>
    <groupId>jakarta.xml.bind</groupId>
    <artifactId>jakarta.xml.bind-api</artifactId>
    <version>3.0.1</version>
</dependency>

// 4.4.0 - Content-Type Change (Breaking!)
// REST Assured no longer adds "charset=utf-8" to application/json
// To restore old behavior:
RestAssured.config = config().encoderConfig(
    encoderConfig().defaultCharsetForContentType("UTF-8", "application/json"));

// 4.3.0 - Groovy 3 Upgrade
// Internal upgrade to Groovy 3.0.x

// 4.2.0 - JSON-B Support & Kotlin MockMvc Extensions
import io.restassured.module.mockmvc.kotlin.extensions.*
Given { mockMvc(mvc); param("name", "Johan") }
When { get("/greeting") }
Then { body("id", equalTo(1)) }

// 4.1.0 - Kotlin Extensions Module
import io.restassured.module.kotlin.extensions.*
val result: String = Given {
    port(7000)
    body("hello")
} When {
    put("/path")
} Then {
    statusCode(200)
} Extract {
    path("message")
}

// 4.0.0 - Package Moves (Breaking!)
// io.restassured.mapper.TypeRef -> io.restassured.common.mapper.TypeRef
// io.restassured.mapper.factory.* -> io.restassured.path.json.mapper.factory.*

// Removed Deprecated Methods:
// - withArguments -> withArgs
// - content -> body
// - specification -> spec
// - formParameters -> formParams`,
          explanation: "Version 4.x modernized REST Assured with Java 8 features, Hamcrest 2, Kotlin support, and JSON-B. Note the package moves and removed deprecated methods."
        },
        {
          id: "version-3x",
          version: "1.0",
          title: "Version 3.x Series (2016-2019)",
          description: "New group ID, package rename, custom HTTP methods",
          code: `// REST Assured 3.0 - Major Rebranding
// Release Date: December 2016

// GROUP ID CHANGE (Breaking!)
// Old (before 3.0):
<dependency>
    <groupId>com.jayway.restassured</groupId>
    <artifactId>rest-assured</artifactId>
</dependency>

// New (3.0+):
<dependency>
    <groupId>io.rest-assured</groupId>
    <artifactId>rest-assured</artifactId>
</dependency>

// PACKAGE RENAME (Breaking!)
// Old: com.jayway.restassured.*
// New: io.restassured.*
// Search and replace in your codebase!

// Custom HTTP Methods Support
when().request("CONNECT", "/somewhere").then().statusCode(200);
when().request(Method.GET, "/lotto").then().statusCode(200);

// ResponseAwareMatcher for Headers
given().
    redirects().follow(false).
when().
    get("/redirect").
then().
    header("Location", response -> endsWith("/redirect/" + response.path("id")));

// All HTTP Verbs Support Body/Multipart
// Even GET, OPTIONS, TRACE now support body and file uploads

// Custom Multipart Boundary
given().
    contentType("multipart/mixed; boundary=abcdef").
    multiPart(file).
when().
    post("/upload");

// 3.3.0 - TypeRef for Generic Deserialization
List<Map<String, Object>> products = get("/products")
    .as(new TypeRef<List<Map<String, Object>>>() {});

// 3.2.0 - OSGi Support & Spring WebTestClient Module

// Migration Steps:
// 1. Change Maven groupId to io.rest-assured
// 2. Search/replace com.jayway.restassured -> io.restassured
// 3. Fix compile errors from removed deprecations`,
          explanation: "Version 3.0 was a major rebranding from Jayway to io.rest-assured. Required package rename and group ID change. Added custom HTTP methods and improved multipart support."
        },
        {
          id: "version-2x",
          version: "1.0",
          title: "Version 2.x & Earlier (2010-2016)",
          description: "Foundation features and original API",
          code: `// REST Assured 2.x - Mature API
// The 2.x series refined the core API

// 2.9.0 - Certificate Auth Improvements
given().
    auth().certificate("keystore.jks", "password",
        certAuthSettings().keyStoreType("PKCS12")).
when().
    get("/secure");

// 2.8.0 - Session Filter
SessionFilter session = new SessionFilter();
given().filter(session).auth().form("user", "pass").post("/login");
given().filter(session).get("/protected");

// 2.5.0 - Response Time Validation
get("/api").then().time(lessThan(2000L));

// 2.4.0 - Failure Listeners
// Custom handling when assertions fail

// 2.0.0 - SSL Improvements
given().relaxedHTTPSValidation().when().get("https://self-signed.example.com");

// REST Assured 1.x - Original Release (2010)
// First public release with core features:
// - GET, POST, PUT, DELETE support
// - JSON and XML parsing with GPath
// - Basic/Digest authentication
// - Request/Response logging
// - Hamcrest matcher integration

// Original Static Imports (unchanged since 1.x)
import static io.restassured.RestAssured.*;
import static org.hamcrest.Matchers.*;

// Core DSL (stable since 1.x)
given().
    param("key", "value").
when().
    get("/endpoint").
then().
    statusCode(200).
    body("path", equalTo("expected"));

// Historical Note:
// REST Assured started as a Jayway project in 2010
// Created by Johan Haleby to simplify REST API testing in Java
// Inspired by Ruby's RSpec and Groovy's testing capabilities`,
          explanation: "Version 2.x and earlier established REST Assured's foundation. The core given/when/then DSL has remained stable since version 1.0, showing excellent API design."
        },
        {
          id: "migration-guide",
          version: "1.0",
          title: "Migration Guide",
          description: "Upgrading between major versions",
          code: `// MIGRATION: 5.x to 6.0
// 1. Upgrade Java to 17+
// 2. Update Spring to 5.3+ (or 6.x/7.x)
// 3. Update Jackson to 3.x (if using)
// 4. No API changes required

// MIGRATION: 4.x to 5.x
// 1. No API changes required
// 2. Internal Groovy upgrade (4.x)
// 3. Test your custom closures

// MIGRATION: 3.x to 4.x
// 1. Upgrade Java to 8+
// 2. Update package imports:
//    io.restassured.mapper.TypeRef -> 
//    io.restassured.common.mapper.TypeRef
// 3. Replace deprecated methods:
//    withArguments -> withArgs
//    withNoArguments -> withNoArgs
//    content -> body
//    specification -> spec
//    root -> rootPath
// 4. Remove long method variants:
//    formParameters -> formParams
//    queryParameters -> queryParams
//    pathParameters -> pathParams

// MIGRATION: 2.x to 3.x
// 1. Change Maven groupId:
//    com.jayway.restassured -> io.rest-assured
// 2. Search and replace in code:
//    com.jayway.restassured -> io.restassured
// 3. Cookie/Header classes moved:
//    com.jayway.restassured.response.Cookie ->
//    io.restassured.http.Cookie
// 4. ObjectMapperType moved:
//    io.restassured.internal.mapper.ObjectMapperType ->
//    io.restassured.mapper.ObjectMapperType

// GENERAL MIGRATION TIPS:
// 1. Check changelog for breaking changes
// 2. Run full test suite after upgrade
// 3. Address deprecation warnings proactively
// 4. Update Groovy if using custom closures`,
          explanation: "Follow version-specific migration steps carefully. Most upgrades are smooth, but major versions (3.0, 4.0, 6.0) had breaking changes requiring code updates."
        }
      ]
    },
    {
      id: "additional-methods",
      title: "Additional Methods",
      icon: Code,
      description: "More utility methods and lesser-known features",
      items: [
        {
          id: "request-method",
          version: "3.0",
          title: "Custom HTTP Methods",
          description: "Use any HTTP verb including custom ones",
          code: `// Standard method using request()
when().
    request("GET", "/resource").
then().
    statusCode(200);

// Using Method enum
import io.restassured.http.Method;

when().
    request(Method.POST, "/resource").
then().
    statusCode(201);

// Custom/non-standard HTTP method
when().
    request("CONNECT", "/tunnel").
then().
    statusCode(200);

when().
    request("PROPFIND", "/webdav/resource").
then().
    statusCode(207);

// With request body
given().
    body("custom payload").
when().
    request("CUSTOM", "/endpoint").
then().
    statusCode(200);`,
          explanation: "The request() method supports any HTTP verb, including non-standard ones like PROPFIND (WebDAV) or custom application-specific methods."
        },
        {
          id: "response-aware-matchers",
          version: "3.0",
          title: "Response-Aware Matchers",
          description: "Matchers that can access response data",
          code: `// Use response data in header validation
given().
    redirects().follow(false).
when().
    get("/redirect").
then().
    header("Location", response -> endsWith("/redirect/" + response.path("id")));

// Custom ResponseAwareMatcher
ResponseAwareMatcher<Response> customMatcher = response -> {
    String expectedValue = response.path("expected");
    return equalTo(expectedValue);
};

get("/endpoint")
.then()
    .body("actual", customMatcher);

// Compare two response paths
get("/comparison")
.then()
    .body("total", response -> equalTo(
        response.path("item1.price") + response.path("item2.price")));

// Validate based on response status
get("/conditional")
.then()
    .body("message", response -> {
        if (response.statusCode() == 200) {
            return equalTo("success");
        }
        return containsString("error");
    });`,
          explanation: "ResponseAwareMatchers receive the full response, allowing dynamic assertions based on other response values. Great for validating computed fields."
        },
        {
          id: "on-fail-message",
          version: "4.5",
          title: "Custom Failure Messages",
          description: "Add context to assertion failures",
          code: `// Add custom message to failures
get("/users/123")
.then()
    .onFailMessage("User 123 should exist and be active")
    .statusCode(200)
    .body("status", equalTo("active"));

// Useful for distinguishing similar tests
get("/orders?status=pending")
.then()
    .onFailMessage("Pending orders query failed")
    .body("orders.size()", greaterThan(0));

get("/orders?status=completed")
.then()
    .onFailMessage("Completed orders query failed")
    .body("orders.size()", greaterThan(0));

// With ResponseSpecBuilder
ResponseSpecification spec = new ResponseSpecBuilder()
    .expectStatusCode(200)
    .build();

get("/api")
.then()
    .onFailMessage("API health check failed!")
    .spec(spec);`,
          explanation: "onFailMessage() adds context to assertion failures, making it easier to identify which test failed and why. Added in version 4.5.0."
        },
        {
          id: "no-content-type",
          version: "4.5",
          title: "No Content Type",
          description: "Remove content-type from requests",
          code: `// Remove content-type header entirely
given().
    noContentType().
    body("raw data").
when().
    post("/raw-endpoint").
then().
    statusCode(200);

// Useful when server doesn't expect content-type
given().
    noContentType().
when().
    post("/legacy-api").
then().
    statusCode(200);

// Override content-type in specification
RequestSpecification spec = new RequestSpecBuilder()
    .setContentType(ContentType.JSON)
    .build();

given().
    spec(spec).
    noContentType().  // Removes JSON content-type
    body("plain text").
when().
    post("/text-endpoint");`,
          explanation: "noContentType() removes the Content-Type header from requests. Useful for legacy APIs or when the server rejects content-type headers."
        },
        {
          id: "detailed-cookies",
          version: "1.4",
          title: "Detailed Cookie Handling",
          description: "Full cookie attribute access and manipulation",
          code: `// Build detailed cookie
Cookie cookie = new Cookie.Builder("session", "abc123")
    .setPath("/")
    .setDomain("example.com")
    .setMaxAge(3600)
    .setSecured(true)
    .setHttpOnly(true)
    .setSameSite("Strict")
    .setComment("Session cookie")
    .setVersion(1)
    .build();

given().
    cookie(cookie).
when().
    get("/dashboard");

// Get detailed cookie from response
Response response = get("/login");
Cookie sessionCookie = response.getDetailedCookie("JSESSIONID");

System.out.println("Path: " + sessionCookie.getPath());
System.out.println("Domain: " + sessionCookie.getDomain());
System.out.println("MaxAge: " + sessionCookie.getMaxAge());
System.out.println("Secured: " + sessionCookie.isSecured());
System.out.println("HttpOnly: " + sessionCookie.isHttpOnly());
System.out.println("SameSite: " + sessionCookie.getSameSite());
System.out.println("Expires: " + sessionCookie.getExpiryDate());

// Get all detailed cookies
Cookies allCookies = response.getDetailedCookies();
for (Cookie c : allCookies) {
    System.out.println(c.getName() + ": " + c.getValue());
}

// Validate detailed cookie attributes
get("/set-cookie")
.then()
    .cookie("session", detailedCookie()
        .path("/")
        .secured(true)
        .httpOnly(true));`,
          explanation: "Detailed cookies provide full access to cookie attributes including SameSite (added in 4.2.0), HttpOnly, Secure, Path, Domain, and Expiry."
        },
        {
          id: "pretty-print",
          version: "1.5",
          title: "Pretty Print & Debug",
          description: "Format and debug response content",
          code: `// Pretty print JSON response
String prettyJson = get("/users").prettyPrint();  // Prints and returns

// Just get pretty string without printing
String formatted = get("/users").asPrettyString();

// Using JsonPath
String json = get("/data").asString();
String pretty = JsonPath.from(json).prettify();
JsonPath.from(json).prettyPrint();  // Prints to stdout

// Using XmlPath
String xml = get("/data.xml").asString();
String prettyXml = XmlPath.from(xml).prettify();
XmlPath.from(xml).prettyPrint();

// Debug with peek()
get("/users")
.then()
    .statusCode(200)
.extract()
    .response()
    .peek()  // Prints response details
    .path("users[0].name");

// Peek at body only
get("/users")
.then()
    .statusCode(200)
.extract()
    .response()
    .prettyPeek()  // Prints formatted body
    .path("users[0].name");`,
          explanation: "Pretty print methods format JSON/XML for readability. peek() and prettyPeek() are useful for debugging while chaining extractions."
        },
        {
          id: "object-mapper-type",
          version: "1.4",
          title: "Object Mapper Configuration",
          description: "Configure serialization libraries",
          code: `// Available ObjectMapperTypes
ObjectMapperType.JACKSON_2  // Jackson 2.x (default for JSON)
ObjectMapperType.GSON       // Google Gson
ObjectMapperType.JOHNZON    // Apache Johnzon
ObjectMapperType.JAXB       // JAXB (default for XML)
ObjectMapperType.JSONB      // Jakarta JSON-B (Eclipse Yasson)

// Explicit mapper for serialization
given().
    body(user, ObjectMapperType.GSON).
when().
    post("/users");

// Explicit mapper for deserialization
User user = get("/users/1").as(User.class, ObjectMapperType.JACKSON_2);

// Set default for all requests
RestAssured.config = config().objectMapperConfig(
    objectMapperConfig().defaultObjectMapperType(ObjectMapperType.GSON));

// Custom Jackson ObjectMapper
ObjectMapper jackson = new ObjectMapper();
jackson.registerModule(new JavaTimeModule());
jackson.setSerializationInclusion(Include.NON_NULL);

RestAssured.config = config().objectMapperConfig(
    objectMapperConfig().jackson2ObjectMapperFactory(
        (cls, charset) -> jackson));

// Custom Gson instance
Gson gson = new GsonBuilder()
    .setDateFormat("yyyy-MM-dd")
    .serializeNulls()
    .create();

RestAssured.config = config().objectMapperConfig(
    objectMapperConfig().gsonObjectMapperFactory(
        (cls, charset) -> gson));`,
          explanation: "REST Assured auto-detects available mappers from classpath. Use ObjectMapperType to explicitly choose, or configure custom instances via factory."
        },
        {
          id: "negative-index",
          version: "5.4",
          title: "Negative Array Indexing",
          description: "Access arrays from the end",
          code: `// Get last element with negative index
String lastEmail = JsonPath.from(json).getString("users[-1].email");

// Get second-to-last
String secondLast = JsonPath.from(json).getString("items[-2].name");

// In body assertions
get("/users")
.then()
    .body("users[-1].name", equalTo("Last User"))
    .body("users[-2].name", equalTo("Second Last"));

// Combined with other operations
get("/orders")
.then()
    .body("orders[-1].items.size()", greaterThan(0))
    .body("orders[-1].total", greaterThan(0.0f));

// Negative index in find operations
get("/products")
.then()
    .body("categories[-1].products.find { it.inStock }.name", 
          notNullValue());`,
          explanation: "Negative indices access arrays from the end. -1 is the last element, -2 is second-to-last, etc. Added in version 5.4.0."
        },
        {
          id: "type-ref",
          version: "3.3",
          title: "TypeRef for Generics",
          description: "Deserialize to generic types",
          code: `// Deserialize to List of Maps
List<Map<String, Object>> users = get("/users")
    .as(new TypeRef<List<Map<String, Object>>>() {});

// Deserialize to List of POJOs
List<User> users = get("/users")
    .as(new TypeRef<List<User>>() {});

// Nested generics
Map<String, List<Order>> ordersByUser = get("/orders/grouped")
    .as(new TypeRef<Map<String, List<Order>>>() {});

// Generic response wrapper
class ApiResponse<T> {
    private T data;
    private String status;
    // getters, setters
}

ApiResponse<List<User>> response = get("/api/users")
    .as(new TypeRef<ApiResponse<List<User>>>() {});

List<User> users = response.getData();

// With JsonPath
List<User> users = JsonPath.from(json)
    .getObject("data.users", new TypeRef<List<User>>() {});`,
          explanation: "TypeRef captures generic type information at runtime, enabling deserialization to List<T>, Map<K,V>, and other generic types. Added in version 3.3.0."
        },
        {
          id: "attributes-session",
          version: "2.3",
          title: "Session Attributes (MockMvc)",
          description: "Set HTTP session attributes in Spring MockMvc tests",
          code: `// Set single session attribute
given().
    sessionAttr("userId", 123).
when().
    get("/profile").
then().
    statusCode(200);

// Set multiple session attributes
Map<String, Object> sessionAttrs = new HashMap<>();
sessionAttrs.put("userId", 123);
sessionAttrs.put("role", "ADMIN");
sessionAttrs.put("preferences", userPrefs);

given().
    sessionAttrs(sessionAttrs).
when().
    get("/admin/dashboard").
then().
    statusCode(200);

// Combined with authentication
given().
    auth().with(user("admin").roles("ADMIN")).
    sessionAttr("lastLogin", new Date()).
when().
    get("/secure/resource").
then().
    statusCode(200);

// Session attribute in specification
MockMvcRequestSpecification spec = new MockMvcRequestSpecBuilder()
    .addSessionAttr("authenticated", true)
    .build();

given().
    spec(spec).
when().
    get("/api/protected");`,
          explanation: "Session attributes allow pre-populating HttpSession in MockMvc tests. Useful for testing session-dependent controllers without going through login flow."
        }
      ]
    },
    {
      id: "file-operations",
      title: "File Operations",
      icon: FileCode,
      description: "File uploads, downloads, and binary data handling",
      items: [
        {
          id: "file-upload",
          title: "File Upload",
          version: "1.3",
          description: "Upload files using multipart requests",
          code: `// Simple file upload
given().
    multiPart(new File("/path/to/file.pdf")).
when().
    post("/upload").
then().
    statusCode(200);

// File with control name
given().
    multiPart("document", new File("/path/to/file.pdf")).
when().
    post("/upload");

// File with content type
given().
    multiPart("image", new File("/path/to/photo.jpg"), "image/jpeg").
when().
    post("/upload");

// Upload from byte array
byte[] pdfContent = Files.readAllBytes(Paths.get("document.pdf"));
given().
    multiPart("file", "document.pdf", pdfContent, "application/pdf").
when().
    post("/upload");

// Upload from InputStream
InputStream stream = getClass().getResourceAsStream("/test-data.json");
given().
    multiPart("data", "test-data.json", stream, "application/json").
when().
    post("/upload");

// Multiple files
given().
    multiPart("files", new File("doc1.pdf")).
    multiPart("files", new File("doc2.pdf")).
    multiPart("files", new File("doc3.pdf")).
when().
    post("/upload-multiple");`,
          explanation: "MultiPart uploads support files, byte arrays, and InputStreams. Each part can have a control name, filename, and content type."
        },
        {
          id: "file-download",
          title: "File Download",
          version: "1.0",
          description: "Download and validate file responses",
          code: `// Download as byte array
byte[] fileContent = get("/download/report.pdf").asByteArray();

// Download and save to file
byte[] data = get("/download/large-file.zip").asByteArray();
Files.write(Paths.get("downloaded.zip"), data);

// Download as InputStream
InputStream stream = get("/download/data.csv").asInputStream();

// Validate file content type
get("/download/image.png")
.then()
    .contentType("image/png")
    .header("Content-Disposition", containsString("attachment"));

// Validate file size via header
get("/download/report.pdf")
.then()
    .header("Content-Length", Integer::parseInt, greaterThan(1000));

// Download with authentication
byte[] secureFile = given().
    auth().basic("user", "pass").
when().
    get("/secure/download/document.pdf").
    asByteArray();`,
          explanation: "Files can be downloaded as byte arrays or InputStreams. Validate response headers like Content-Type, Content-Length, and Content-Disposition."
        },
        {
          id: "binary-body",
          title: "Binary Request Body",
          version: "1.0",
          description: "Send binary data in request body",
          code: `// Send byte array as body
byte[] binaryData = Files.readAllBytes(Paths.get("data.bin"));
given().
    contentType("application/octet-stream").
    body(binaryData).
when().
    post("/binary-endpoint").
then().
    statusCode(200);

// Send file content directly
given().
    contentType("application/pdf").
    body(new File("document.pdf")).
when().
    post("/pdf-processor");

// Base64 encoded binary
String base64Image = Base64.getEncoder().encodeToString(imageBytes);
given().
    contentType(ContentType.JSON).
    body("{ \\"image\\": \\"" + base64Image + "\\" }").
when().
    post("/upload-base64");

// Verify binary response
byte[] response = given().
    contentType("application/octet-stream").
    body(requestData).
when().
    post("/transform").
then().
    contentType("application/octet-stream").
    extract().asByteArray();

assertThat(response.length, greaterThan(0));`,
          explanation: "Binary data can be sent using body() with byte arrays or File objects. Set Content-Type to application/octet-stream for raw binary."
        },
        {
          id: "streaming-response",
          title: "Streaming Responses",
          version: "2.0",
          description: "Handle large streaming responses efficiently",
          code: `// Get response as InputStream (memory efficient)
InputStream stream = get("/large-file").asInputStream();

// Process stream without loading into memory
try (InputStream is = get("/stream").asInputStream();
     BufferedReader reader = new BufferedReader(new InputStreamReader(is))) {
    String line;
    while ((line = reader.readLine()) != null) {
        processLine(line);
    }
}

// Stream to file
try (InputStream is = get("/download").asInputStream();
     FileOutputStream fos = new FileOutputStream("output.bin")) {
    byte[] buffer = new byte[8192];
    int bytesRead;
    while ((bytesRead = is.read(buffer)) != -1) {
        fos.write(buffer, 0, bytesRead);
    }
}

// Validate streaming response headers
get("/stream/events")
.then()
    .contentType("text/event-stream")
    .header("Cache-Control", "no-cache")
    .header("Connection", "keep-alive");`,
          explanation: "Use asInputStream() for memory-efficient handling of large files. Avoid asByteArray() or asString() for large responses to prevent OutOfMemoryError."
        }
      ]
    },
    {
      id: "data-driven",
      title: "Data-Driven Testing",
      icon: Database,
      description: "Parameterized tests and data providers",
      items: [
        {
          id: "parameterized-tests",
          title: "Parameterized Tests",
          version: "1.0",
          description: "Run same test with different data",
          code: `// JUnit 5 @ParameterizedTest
@ParameterizedTest
@CsvSource({
    "admin, admin123, 200",
    "user, user123, 200",
    "guest, guest123, 403",
    "invalid, wrong, 401"
})
void testLogin(String username, String password, int expectedStatus) {
    given().
        formParam("username", username).
        formParam("password", password).
    when().
        post("/login").
    then().
        statusCode(expectedStatus);
}

// TestNG @DataProvider
@DataProvider(name = "userCredentials")
public Object[][] credentials() {
    return new Object[][] {
        {"admin", "admin123", 200},
        {"user", "user123", 200},
        {"guest", "guest123", 403}
    };
}

@Test(dataProvider = "userCredentials")
public void testLoginWithProvider(String user, String pass, int status) {
    given().
        formParam("username", user).
        formParam("password", pass).
    when().
        post("/login").
    then().
        statusCode(status);
}

// External file data source
@ParameterizedTest
@CsvFileSource(resources = "/test-data.csv", numLinesToSkip = 1)
void testFromCSV(String endpoint, int expectedStatus, String expectedBody) {
    get(endpoint)
    .then()
        .statusCode(expectedStatus)
        .body(containsString(expectedBody));
}`,
          explanation: "Parameterized tests run the same test logic with different data sets. Use CSV, method source, or TestNG DataProvider for test data."
        },
        {
          id: "json-test-data",
          title: "JSON Test Data",
          version: "1.4",
          description: "Load test data from JSON files",
          code: `// Load request body from JSON file
given().
    contentType(ContentType.JSON).
    body(new File("src/test/resources/request-body.json")).
when().
    post("/api/users");

// Load and modify JSON data
String jsonTemplate = new String(Files.readAllBytes(
    Paths.get("src/test/resources/user-template.json")));
String requestBody = jsonTemplate
    .replace("{{name}}", "John")
    .replace("{{email}}", "john@test.com");

given().
    contentType(ContentType.JSON).
    body(requestBody).
when().
    post("/api/users");

// Using ObjectMapper for complex data
ObjectMapper mapper = new ObjectMapper();
User user = mapper.readValue(
    new File("src/test/resources/user.json"), User.class);
user.setEmail("updated@test.com");

given().
    contentType(ContentType.JSON).
    body(user).
when().
    put("/api/users/" + user.getId());

// Validate against expected JSON file
get("/api/users/123")
.then()
    .body(equalTo(new String(Files.readAllBytes(
        Paths.get("src/test/resources/expected-user.json")))));`,
          explanation: "External JSON files help separate test data from test logic. Use templates with placeholders for dynamic values."
        },
        {
          id: "response-chaining",
          title: "Response Chaining",
          version: "1.5",
          description: "Use response data in subsequent requests",
          code: `// Extract ID from create response
String userId = given().
    contentType(ContentType.JSON).
    body("{ \\"name\\": \\"John\\" }").
when().
    post("/api/users").
then().
    statusCode(201).
    extract().path("id");

// Use extracted ID in next request
given().
    pathParam("id", userId).
when().
    get("/api/users/{id}").
then().
    statusCode(200).
    body("name", equalTo("John"));

// Chain multiple extractions
ExtractableResponse<Response> createResponse = given().
    contentType(ContentType.JSON).
    body(newUser).
when().
    post("/api/users").
then().
    statusCode(201).
    extract();

String id = createResponse.path("id");
String location = createResponse.header("Location");

// Delete and verify
delete(location).then().statusCode(204);
get("/api/users/" + id).then().statusCode(404);

// Complex workflow
String token = post("/auth/login").path("token");
String orderId = given().
    header("Authorization", "Bearer " + token).
    body(orderRequest).
when().
    post("/orders").
    path("orderId");

given().
    header("Authorization", "Bearer " + token).
when().
    post("/orders/" + orderId + "/confirm").
then().
    statusCode(200);`,
          explanation: "Extract values from responses using path() and use them in subsequent requests. Essential for testing CRUD workflows and authenticated flows."
        },
        {
          id: "dynamic-values",
          title: "Dynamic Test Values",
          version: "1.0",
          description: "Generate unique test data",
          code: `// Unique identifiers
String uniqueEmail = "user_" + System.currentTimeMillis() + "@test.com";
String uuid = UUID.randomUUID().toString();

given().
    contentType(ContentType.JSON).
    body("{ \\"email\\": \\"" + uniqueEmail + "\\", \\"id\\": \\"" + uuid + "\\" }").
when().
    post("/api/users");

// Random data generation (with java-faker)
Faker faker = new Faker();
Map<String, Object> user = new HashMap<>();
user.put("firstName", faker.name().firstName());
user.put("lastName", faker.name().lastName());
user.put("email", faker.internet().emailAddress());
user.put("phone", faker.phoneNumber().phoneNumber());

given().
    contentType(ContentType.JSON).
    body(user).
when().
    post("/api/users");

// Timestamp-based data
LocalDateTime now = LocalDateTime.now();
String startDate = now.format(DateTimeFormatter.ISO_DATE_TIME);
String endDate = now.plusDays(7).format(DateTimeFormatter.ISO_DATE_TIME);

given().
    queryParam("startDate", startDate).
    queryParam("endDate", endDate).
when().
    get("/api/events");

// Sequential test data
AtomicInteger counter = new AtomicInteger(1);
String orderId = "ORD-" + counter.getAndIncrement();`,
          explanation: "Generate unique test data using timestamps, UUIDs, or libraries like Java Faker. Prevents test data collisions in parallel execution."
        }
      ]
    },
    {
      id: "assertions-advanced",
      title: "Advanced Assertions",
      icon: Check,
      description: "Complex validation patterns",
      items: [
        {
          id: "array-assertions",
          title: "Array Assertions",
          version: "1.0",
          description: "Validate JSON arrays thoroughly",
          code: `// Array size
get("/users")
.then()
    .body("$", hasSize(10))
    .body("$.size()", equalTo(10))
    .body("users.size()", greaterThan(0));

// Every item in array
get("/users")
.then()
    .body("users.email", everyItem(containsString("@")))
    .body("users.age", everyItem(greaterThan(0)))
    .body("users.status", everyItem(oneOf("active", "inactive")));

// Find specific item in array
get("/products")
.then()
    .body("find { it.id == 123 }.name", equalTo("Widget"))
    .body("findAll { it.price > 100 }.size()", equalTo(5));

// First and last items
get("/items")
.then()
    .body("[0].name", equalTo("First Item"))
    .body("[-1].name", equalTo("Last Item"));

// Array contains item with properties
get("/orders")
.then()
    .body("orders", hasItem(
        allOf(
            hasEntry("status", "completed"),
            hasEntry(equalTo("total"), greaterThan(100.0))
        )
    ));

// Sort validation
get("/leaderboard")
.then()
    .body("scores", contains(100, 95, 90, 85, 80));  // Exact order

// Unique values
List<String> ids = get("/users").path("users.id");
assertThat(ids, hasSize(new HashSet<>(ids).size()));`,
          explanation: "Array assertions include size checks, iteration validation, filtering with closures, and order verification. Use everyItem() for bulk validation."
        },
        {
          id: "nested-json",
          title: "Nested JSON Validation",
          version: "1.0",
          description: "Validate deeply nested structures",
          code: `// Deep nested path
get("/organization")
.then()
    .body("company.departments[0].teams[0].lead.name", equalTo("John"))
    .body("company.departments.find { it.name == 'Engineering' }.budget", greaterThan(1000000));

// Multiple levels with root
get("/data")
.then()
    .root("response.data.user")
    .body("profile.firstName", equalTo("John"))
    .body("profile.lastName", equalTo("Doe"))
    .body("settings.notifications.email", equalTo(true))
    .body("settings.notifications.sms", equalTo(false));

// Nested array of objects
get("/orders")
.then()
    .body("orders.items.flatten().name", hasItems("Product A", "Product B"))
    .body("orders.items.flatten().findAll { it.quantity > 1 }.size()", greaterThan(0));

// Validate nested structure exists
get("/user/123")
.then()
    .body("$", hasKey("profile"))
    .body("profile", hasKey("address"))
    .body("profile.address", hasKey("city"));

// Nested with wildcards
get("/catalog")
.then()
    .body("categories.*.products.*.price", everyItem(greaterThan(0.0f)));`,
          explanation: "Navigate deeply nested JSON using dot notation. Use root() to reduce repetition. The flatten() method combines nested arrays."
        },
        {
          id: "conditional-assertions",
          title: "Conditional Assertions",
          version: "2.5",
          description: "Assert based on conditions",
          code: `// Response-aware assertions
get("/user/123")
.then()
    .body("status", response -> {
        if (response.path("type").equals("admin")) {
            return equalTo("active");
        }
        return anyOf(equalTo("active"), equalTo("pending"));
    });

// Conditional validation based on status
Response response = get("/api/resource");
if (response.statusCode() == 200) {
    response.then()
        .body("data", notNullValue())
        .body("errors", nullValue());
} else {
    response.then()
        .body("errors", notNullValue())
        .body("data", nullValue());
}

// Using soft assertions (AssertJ)
SoftAssertions softly = new SoftAssertions();
Response resp = get("/users/123");

softly.assertThat(resp.statusCode()).isEqualTo(200);
softly.assertThat(resp.path("name").toString()).isNotEmpty();
softly.assertThat(resp.path("email").toString()).contains("@");
softly.assertAll();  // Reports all failures at once

// Custom matcher with lambda
get("/data")
.then()
    .body("items", items -> {
        List<Map<String, Object>> list = (List<Map<String, Object>>) items;
        return list.stream()
            .allMatch(item -> item.get("valid").equals(true));
    });`,
          explanation: "Conditional assertions allow dynamic validation based on response content. Use soft assertions to collect all failures before reporting."
        },
        {
          id: "date-time-assertions",
          title: "Date/Time Assertions",
          version: "1.0",
          description: "Validate date and time fields",
          code: `// ISO date format validation
get("/events")
.then()
    .body("createdAt", matchesPattern("\\\\d{4}-\\\\d{2}-\\\\d{2}T\\\\d{2}:\\\\d{2}:\\\\d{2}.*"));

// Date comparison
String today = LocalDate.now().toString();
get("/today-events")
.then()
    .body("date", startsWith(today));

// Parse and compare dates
String createdAt = get("/user/123").path("createdAt");
LocalDateTime created = LocalDateTime.parse(createdAt, DateTimeFormatter.ISO_DATE_TIME);
assertThat(created, is(lessThan(LocalDateTime.now())));

// Date within range
get("/recent-orders")
.then()
    .body("orders.createdAt", everyItem(
        matchesPattern("2024-.*")  // All from 2024
    ));

// Relative date validation
Instant now = Instant.now();
Instant oneHourAgo = now.minus(1, ChronoUnit.HOURS);

get("/recent-activity")
.then()
    .body("activities.findAll { Instant.parse(it.timestamp).isAfter(Instant.parse('" + oneHourAgo + "')) }.size()", greaterThan(0));

// Timestamp ordering
List<String> timestamps = get("/logs").path("entries.timestamp");
for (int i = 1; i < timestamps.size(); i++) {
    assertThat(timestamps.get(i), greaterThan(timestamps.get(i-1)));
}`,
          explanation: "Date assertions often use regex patterns for format validation. Parse dates for comparison operations and range checks."
        },
        {
          id: "schema-partial",
          title: "Partial Schema Validation",
          version: "2.1",
          description: "Validate specific parts of response",
          code: `// Validate only specific fields exist
get("/user/123")
.then()
    .body("$", hasKey("id"))
    .body("$", hasKey("name"))
    .body("$", hasKey("email"))
    .body("$", not(hasKey("password")));  // Sensitive field not exposed

// Validate field types
get("/product/456")
.then()
    .body("id", instanceOf(Integer.class))
    .body("name", instanceOf(String.class))
    .body("price", instanceOf(Float.class))
    .body("inStock", instanceOf(Boolean.class))
    .body("tags", instanceOf(List.class));

// Nullable fields
get("/user/123")
.then()
    .body("middleName", anyOf(nullValue(), instanceOf(String.class)))
    .body("phone", anyOf(nullValue(), matchesPattern("\\\\+?\\\\d{10,15}")));

// Validate structure without exact values
get("/api/status")
.then()
    .body("status", notNullValue())
    .body("timestamp", notNullValue())
    .body("version", matchesPattern("\\\\d+\\\\.\\\\d+\\\\.\\\\d+"))
    .body("services", hasSize(greaterThan(0)))
    .body("services.name", everyItem(notNullValue()));

// Contract validation (key fields only)
get("/order/789")
.then()
    .body("orderId", notNullValue())
    .body("customerId", notNullValue())
    .body("items", hasSize(greaterThan(0)))
    .body("total", greaterThan(0.0f))
    .body("status", isIn(Arrays.asList("pending", "processing", "shipped", "delivered")));`,
          explanation: "Partial validation checks structure and key fields without requiring exact values. Useful for API contract testing."
        }
      ]
    },
    {
      id: "test-patterns",
      title: "Testing Patterns",
      icon: Terminal,
      description: "Best practices and design patterns",
      items: [
        {
          id: "base-test-class",
          title: "Base Test Class",
          version: "1.0",
          description: "Reusable test configuration",
          code: `// Base test class for all API tests
public abstract class BaseApiTest {
    
    @BeforeAll
    static void setup() {
        RestAssured.baseURI = System.getProperty("api.baseUri", "http://localhost:8080");
        RestAssured.basePath = "/api/v1";
        RestAssured.enableLoggingOfRequestAndResponseIfValidationFails();
        
        RestAssured.config = RestAssured.config()
            .encoderConfig(encoderConfig().defaultContentCharset("UTF-8"))
            .decoderConfig(decoderConfig().defaultContentCharset("UTF-8"))
            .logConfig(logConfig().blacklistHeader("Authorization"));
    }
    
    @AfterAll
    static void teardown() {
        RestAssured.reset();
    }
    
    protected RequestSpecification givenAuth() {
        return given()
            .contentType(ContentType.JSON)
            .header("Authorization", "Bearer " + getAuthToken());
    }
    
    protected abstract String getAuthToken();
}

// Concrete test class
public class UserApiTest extends BaseApiTest {
    
    @Override
    protected String getAuthToken() {
        return TokenProvider.getToken();
    }
    
    @Test
    void shouldGetUser() {
        givenAuth().
        when().
            get("/users/123").
        then().
            statusCode(200);
    }
}`,
          explanation: "Base test classes reduce duplication and centralize configuration. Override methods for environment-specific settings."
        },
        {
          id: "page-object-api",
          title: "API Object Pattern",
          version: "1.0",
          description: "Encapsulate API endpoints in objects",
          code: `// API object for User endpoints
public class UserApi {
    private static final String USERS_PATH = "/users";
    
    public static Response createUser(User user) {
        return given().
            contentType(ContentType.JSON).
            body(user).
        when().
            post(USERS_PATH);
    }
    
    public static Response getUser(String userId) {
        return given().
            pathParam("id", userId).
        when().
            get(USERS_PATH + "/{id}");
    }
    
    public static Response updateUser(String userId, User user) {
        return given().
            contentType(ContentType.JSON).
            pathParam("id", userId).
            body(user).
        when().
            put(USERS_PATH + "/{id}");
    }
    
    public static Response deleteUser(String userId) {
        return given().
            pathParam("id", userId).
        when().
            delete(USERS_PATH + "/{id}");
    }
    
    public static Response searchUsers(Map<String, String> filters) {
        RequestSpecification spec = given();
        filters.forEach(spec::queryParam);
        return spec.when().get(USERS_PATH);
    }
}

// Usage in tests
@Test
void crudWorkflow() {
    User user = new User("John", "john@test.com");
    
    // Create
    String userId = UserApi.createUser(user)
        .then().statusCode(201)
        .extract().path("id");
    
    // Read
    UserApi.getUser(userId)
        .then().statusCode(200)
        .body("name", equalTo("John"));
    
    // Update
    user.setName("John Updated");
    UserApi.updateUser(userId, user)
        .then().statusCode(200);
    
    // Delete
    UserApi.deleteUser(userId)
        .then().statusCode(204);
}`,
          explanation: "API objects encapsulate endpoint details and provide a clean interface. Tests become more readable and maintainable."
        },
        {
          id: "builder-pattern",
          title: "Request Builder Pattern",
          version: "1.5",
          description: "Fluent request construction",
          code: `// Custom request builder
public class ApiRequestBuilder {
    private RequestSpecification spec;
    
    public ApiRequestBuilder() {
        this.spec = given()
            .contentType(ContentType.JSON)
            .accept(ContentType.JSON);
    }
    
    public ApiRequestBuilder withAuth(String token) {
        spec.header("Authorization", "Bearer " + token);
        return this;
    }
    
    public ApiRequestBuilder withQueryParam(String key, Object value) {
        spec.queryParam(key, value);
        return this;
    }
    
    public ApiRequestBuilder withBody(Object body) {
        spec.body(body);
        return this;
    }
    
    public ApiRequestBuilder withHeader(String name, String value) {
        spec.header(name, value);
        return this;
    }
    
    public ApiRequestBuilder withLogging() {
        spec.log().all();
        return this;
    }
    
    public Response get(String path) {
        return spec.when().get(path);
    }
    
    public Response post(String path) {
        return spec.when().post(path);
    }
    
    public Response put(String path) {
        return spec.when().put(path);
    }
    
    public Response delete(String path) {
        return spec.when().delete(path);
    }
}

// Usage
@Test
void builderExample() {
    new ApiRequestBuilder()
        .withAuth(getToken())
        .withQueryParam("status", "active")
        .withLogging()
        .get("/users")
        .then()
        .statusCode(200);
}`,
          explanation: "Custom builders add domain-specific methods while maintaining fluent syntax. Great for complex request configurations."
        },
        {
          id: "retry-pattern",
          title: "Retry Pattern",
          version: "1.0",
          description: "Handle flaky tests with retries",
          code: `// Simple retry utility
public class RetryUtil {
    
    public static <T> T retry(Supplier<T> action, int maxRetries, Duration delay) {
        Exception lastException = null;
        for (int i = 0; i < maxRetries; i++) {
            try {
                return action.get();
            } catch (Exception e) {
                lastException = e;
                try {
                    Thread.sleep(delay.toMillis());
                } catch (InterruptedException ie) {
                    Thread.currentThread().interrupt();
                }
            }
        }
        throw new RuntimeException("Action failed after " + maxRetries + " retries", lastException);
    }
}

// Usage for eventually consistent systems
@Test
void eventuallyConsistent() {
    // Create resource
    String id = post("/async-resource").path("id");
    
    // Retry until resource is ready
    RetryUtil.retry(() -> {
        get("/resources/" + id)
            .then()
            .statusCode(200)
            .body("status", equalTo("ready"));
        return null;
    }, 5, Duration.ofSeconds(2));
}

// Using Awaitility library
@Test
void withAwaitility() {
    String orderId = post("/orders").path("id");
    
    await()
        .atMost(30, TimeUnit.SECONDS)
        .pollInterval(2, TimeUnit.SECONDS)
        .until(() -> {
            Response response = get("/orders/" + orderId);
            return response.statusCode() == 200 && 
                   response.path("status").equals("processed");
        });
}

// Conditional retry based on response
public Response getWithRetry(String path, int expectedStatus) {
    for (int i = 0; i < 3; i++) {
        Response response = get(path);
        if (response.statusCode() == expectedStatus) {
            return response;
        }
        sleep(1000);
    }
    throw new AssertionError("Expected status " + expectedStatus + " not achieved");
}`,
          explanation: "Retries handle eventual consistency and transient failures. Use Awaitility for cleaner async polling patterns."
        },
        {
          id: "cleanup-pattern",
          title: "Test Data Cleanup",
          version: "1.0",
          description: "Ensure test isolation with cleanup",
          code: `// Cleanup using @AfterEach
public class UserApiTest {
    private List<String> createdUserIds = new ArrayList<>();
    
    @AfterEach
    void cleanup() {
        for (String userId : createdUserIds) {
            try {
                delete("/users/" + userId);
            } catch (Exception e) {
                // Log but don't fail cleanup
                System.err.println("Cleanup failed for user: " + userId);
            }
        }
        createdUserIds.clear();
    }
    
    @Test
    void createUserTest() {
        String userId = given().
            body(newUser).
        when().
            post("/users").
        then().
            extract().path("id");
        
        createdUserIds.add(userId);  // Track for cleanup
        
        // Assertions...
    }
}

// Using try-with-resources pattern
public class TestResource implements AutoCloseable {
    private final String resourceId;
    
    public TestResource(String resourceId) {
        this.resourceId = resourceId;
    }
    
    @Override
    public void close() {
        delete("/resources/" + resourceId);
    }
    
    public static TestResource create(Object data) {
        String id = given().body(data).post("/resources").path("id");
        return new TestResource(id);
    }
}

// Usage
@Test
void resourceTest() {
    try (TestResource resource = TestResource.create(testData)) {
        get("/resources/" + resource.resourceId)
            .then()
            .statusCode(200);
    }  // Auto-cleanup
}`,
          explanation: "Cleanup ensures test isolation by removing created data. Use try-with-resources for automatic cleanup even on test failure."
        }
      ]
    },
    {
      id: "error-handling",
      title: "Error Handling",
      icon: Shield,
      description: "Validate error responses and edge cases",
      items: [
        {
          id: "error-response",
          title: "Error Response Validation",
          version: "1.0",
          description: "Validate error responses and messages",
          code: `// Validate 4xx client errors
given().
    body("{ \\"invalid\\": \\"json }").  // Malformed JSON
when().
    post("/api/users").
then().
    statusCode(400).
    body("error", equalTo("Bad Request")).
    body("message", containsString("JSON"));

// Validate 404 Not Found
get("/api/users/nonexistent")
.then()
    .statusCode(404)
    .body("error", equalTo("Not Found"))
    .body("path", equalTo("/api/users/nonexistent"));

// Validate 401 Unauthorized
get("/api/protected")
.then()
    .statusCode(401)
    .body("error", equalTo("Unauthorized"))
    .header("WWW-Authenticate", notNullValue());

// Validate 403 Forbidden
given().
    header("Authorization", "Bearer user-token").
when().
    delete("/api/admin/resource").
then().
    statusCode(403).
    body("error", equalTo("Forbidden")).
    body("message", containsString("permission"));

// Validate 422 Unprocessable Entity
given().
    contentType(ContentType.JSON).
    body("{ \\"email\\": \\"invalid-email\\" }").
when().
    post("/api/users").
then().
    statusCode(422).
    body("errors.field", hasItem("email")).
    body("errors.message", hasItem(containsString("valid email")));

// Validate 500 Server Error handling
given().
    header("X-Force-Error", "true").  // Test header
when().
    get("/api/unstable").
then().
    statusCode(500).
    body("error", equalTo("Internal Server Error")).
    body("timestamp", notNullValue()).
    body("path", notNullValue());`,
          explanation: "Error responses should have consistent structure with error code, message, and relevant details. Test all expected error scenarios."
        },
        {
          id: "validation-errors",
          title: "Validation Error Testing",
          version: "1.0",
          description: "Test field validation thoroughly",
          code: `// Missing required field
given().
    contentType(ContentType.JSON).
    body("{ \\"lastName\\": \\"Doe\\" }").  // Missing firstName
when().
    post("/api/users").
then().
    statusCode(400).
    body("errors.find { it.field == 'firstName' }.message", 
         equalTo("firstName is required"));

// Invalid field format
given().
    contentType(ContentType.JSON).
    body("{ \\"email\\": \\"not-an-email\\" }").
when().
    post("/api/users").
then().
    statusCode(400).
    body("errors.find { it.field == 'email' }.code", equalTo("INVALID_FORMAT"));

// Field too long
given().
    contentType(ContentType.JSON).
    body("{ \\"name\\": \\"" + "x".repeat(256) + "\\" }").
when().
    post("/api/users").
then().
    statusCode(400).
    body("errors.find { it.field == 'name' }.message", 
         containsString("255 characters"));

// Multiple validation errors
given().
    contentType(ContentType.JSON).
    body("{ \\"email\\": \\"bad\\", \\"age\\": -1 }").
when().
    post("/api/users").
then().
    statusCode(400).
    body("errors.size()", greaterThanOrEqualTo(2)).
    body("errors.field", hasItems("email", "age"));

// Boundary testing
@ParameterizedTest
@CsvSource({
    "0, 400",    // Below minimum
    "1, 200",    // At minimum (valid)
    "120, 200",  // At maximum (valid)
    "121, 400"   // Above maximum
})
void ageValidation(int age, int expectedStatus) {
    given().
        contentType(ContentType.JSON).
        body("{ \\"name\\": \\"Test\\", \\"age\\": " + age + " }").
    when().
        post("/api/users").
    then().
        statusCode(expectedStatus);
}`,
          explanation: "Validation testing covers required fields, format validation, length limits, and boundary conditions. Test both valid and invalid cases."
        },
        {
          id: "timeout-handling",
          title: "Timeout Handling",
          version: "2.0",
          description: "Test and handle request timeouts",
          code: `// Configure request timeout
RestAssured.config = RestAssured.config()
    .httpClient(httpClientConfig()
        .setParam("http.connection.timeout", 5000)   // Connection timeout
        .setParam("http.socket.timeout", 10000));    // Socket timeout

// Per-request timeout (using filter)
given().
    config(config().httpClient(httpClientConfig()
        .setParam("http.socket.timeout", 3000))).
when().
    get("/slow-endpoint").
then().
    statusCode(anyOf(is(200), is(504)));  // OK or Gateway Timeout

// Test timeout behavior
@Test
void shouldTimeoutForSlowEndpoint() {
    given().
        config(config().httpClient(httpClientConfig()
            .setParam("http.socket.timeout", 1000))).
    when().
        get("/delay/5000").  // 5 second delay
    then().
        time(lessThan(2000L));  // Should timeout before 2s
}

// Handling SocketTimeoutException
@Test
void handleTimeout() {
    try {
        given().
            config(config().httpClient(httpClientConfig()
                .setParam("http.socket.timeout", 100))).
        when().
            get("/slow");
        fail("Expected timeout");
    } catch (Exception e) {
        assertThat(e.getCause(), instanceOf(SocketTimeoutException.class));
    }
}

// Async timeout testing
@Test
void asyncTimeout() {
    given().
        config(config().asyncConfig(
            asyncConfig().timeout(1, TimeUnit.SECONDS))).
    when().
        async().get("/slow-async").
    then().
        statusCode(200);  // Or handle timeout exception
}`,
          explanation: "Configure connection and socket timeouts to handle slow responses. Test that timeouts trigger appropriately."
        },
        {
          id: "rate-limiting",
          title: "Rate Limiting Tests",
          version: "1.0",
          description: "Test API rate limiting behavior",
          code: `// Test rate limit headers
get("/api/resource")
.then()
    .statusCode(200)
    .header("X-RateLimit-Limit", notNullValue())
    .header("X-RateLimit-Remaining", notNullValue())
    .header("X-RateLimit-Reset", notNullValue());

// Extract and use rate limit info
Response response = get("/api/resource");
int remaining = Integer.parseInt(response.header("X-RateLimit-Remaining"));
if (remaining < 10) {
    System.out.println("Warning: Rate limit almost exhausted");
}

// Test rate limit exceeded
@Test
void shouldReturn429WhenRateLimitExceeded() {
    // Make requests until rate limited
    for (int i = 0; i < 100; i++) {
        Response response = get("/api/rate-limited");
        if (response.statusCode() == 429) {
            response.then()
                .body("error", equalTo("Too Many Requests"))
                .header("Retry-After", notNullValue());
            return;  // Test passed
        }
    }
    fail("Rate limit was not enforced");
}

// Respect rate limits in tests
public class RateLimitedApiClient {
    private long lastRequestTime = 0;
    private int minIntervalMs = 100;  // 10 requests per second
    
    public synchronized Response get(String path) {
        long elapsed = System.currentTimeMillis() - lastRequestTime;
        if (elapsed < minIntervalMs) {
            sleep(minIntervalMs - elapsed);
        }
        lastRequestTime = System.currentTimeMillis();
        return RestAssured.get(path);
    }
}`,
          explanation: "Rate limiting tests verify header presence and 429 responses. In test suites, respect rate limits to avoid flaky tests."
        }
      ]
    },
    {
      id: "xml-processing",
      title: "XML Processing",
      icon: FileCode,
      description: "Advanced XML parsing and validation",
      items: [
        {
          id: "xml-namespaces",
          title: "XML Namespaces",
          version: "1.0",
          description: "Handle namespaced XML documents",
          code: `// Declare namespace for validation
given().
    config(RestAssured.config().xmlConfig(
        xmlConfig().declareNamespace("ns", "http://example.com/api"))).
when().
    get("/namespaced-xml").
then().
    body("root.ns:element.text()", equalTo("value"));

// Multiple namespaces
given().
    config(config().xmlConfig(xmlConfig()
        .declareNamespace("soap", "http://schemas.xmlsoap.org/soap/envelope/")
        .declareNamespace("api", "http://api.example.com"))).
when().
    get("/soap-response").
then().
    body("soap:Envelope.soap:Body.api:Response.status", equalTo("success"));

// XPath with namespaces
NamespaceContext nsContext = new NamespaceContext() {
    public String getNamespaceURI(String prefix) {
        return "http://example.com/" + prefix;
    }
    // ... other methods
};

get("/xml")
.then()
    .body(hasXPath("/ns:root/ns:item", nsContext));

// Extract namespaced value
XmlPath xml = get("/namespaced").xmlPath();
xml.setConfig(xmlPathConfig().declareNamespace("ns", "http://example.com"));
String value = xml.getString("root.ns:element");`,
          explanation: "XML namespaces must be declared in config before use. Use XmlConfig for REST Assured or configure XmlPath directly for extraction."
        },
        {
          id: "xpath-validation",
          title: "XPath Validation",
          version: "1.0",
          description: "Validate XML using XPath expressions",
          code: `// Basic XPath assertion
get("/greeting")
.then()
    .body(hasXPath("/greeting/firstName", containsString("John")));

// XPath with text content check
get("/greeting")
.then()
    .body(hasXPath("/greeting/firstName[text()='John']"));

// XPath with attribute check
get("/items")
.then()
    .body(hasXPath("//item[@id='123']"))
    .body(hasXPath("//item[@status='active']/@id"));

// XPath count
get("/orders")
.then()
    .body(hasXPath("count(//order)", equalTo("5")));

// XPath with predicates
get("/products")
.then()
    .body(hasXPath("//product[price > 100]/name"))
    .body(hasXPath("//product[contains(name, 'Widget')]"));

// Multiple XPath validations
get("/catalog")
.then()
    .body(hasXPath("/catalog/categories/category"))
    .body(hasXPath("/catalog/categories/category[@type='electronics']"))
    .body(hasXPath("//item[position()=1]/name", equalTo("First Item")));

// XPath functions
get("/data")
.then()
    .body(hasXPath("string-length(//name) > 0"))
    .body(hasXPath("sum(//price) > 100"));`,
          explanation: "XPath provides powerful XML querying with support for predicates, functions, and attribute selection. Use hasXPath() matcher for validation."
        },
        {
          id: "xml-gpath",
          title: "GPath for XML",
          version: "1.0",
          description: "Use Groovy GPath syntax for XML",
          code: `// Basic GPath navigation
get("/shopping")
.then()
    .body("shopping.category.item", hasItems("Chocolate", "Coffee"));

// Find with closure
get("/shopping")
.then()
    .body("shopping.category.find { it.@type == 'groceries' }.item", 
          hasItems("Chocolate", "Coffee"));

// Depth-first search with **
get("/shopping")
.then()
    .body("**.find { it.@type == 'groceries' }", hasItems("Chocolate", "Coffee"));

// Get attribute value
get("/products")
.then()
    .body("products.product.@id", hasItem("123"))
    .body("products.product.find { it.@id == '123' }.name", equalTo("Widget"));

// Text content
get("/greeting")
.then()
    .body("greeting.firstName.text()", equalTo("John"))
    .body("greeting.lastName.text()", equalTo("Doe"));

// Size and aggregation
get("/items")
.then()
    .body("items.item.size()", equalTo(5))
    .body("items.item.findAll { it.price.toFloat() > 10 }.size()", greaterThan(0));

// Collect values
List<String> names = get("/items").xmlPath()
    .getList("items.item.name");`,
          explanation: "GPath provides Groovy-style XML navigation. Use @ for attributes, text() for text content, and closures for filtering."
        },
        {
          id: "xsd-dtd-validation",
          title: "XSD & DTD Validation",
          version: "1.1",
          description: "Validate XML against schemas",
          code: `// XSD validation from file
get("/products")
.then()
    .body(matchesXsd(new File("schemas/products.xsd")));

// XSD from classpath
get("/products")
.then()
    .body(matchesXsdInClasspath("schemas/products.xsd"));

// XSD from string
String xsd = "<?xml version='1.0'?><xs:schema ...>...</xs:schema>";
get("/products")
.then()
    .body(matchesXsd(xsd));

// DTD validation
get("/videos")
.then()
    .body(matchesDtd(new File("schemas/videos.dtd")));

// DTD from classpath
get("/videos")
.then()
    .body(matchesDtdInClasspath("schemas/videos.dtd"));

// Combined validation
get("/catalog")
.then()
    .contentType(ContentType.XML)
    .body(matchesXsdInClasspath("catalog.xsd"))
    .body("catalog.items.size()", greaterThan(0));

// Static import for matchers
import static io.restassured.matcher.RestAssuredMatchers.*;`,
          explanation: "XSD validates XML structure and data types. DTD validates document structure. Both ensure API responses conform to contracts."
        }
      ]
    },
    {
      id: "response-extraction",
      title: "Response Extraction",
      icon: Database,
      description: "Extract and store response data",
      items: [
        {
          id: "extract-values",
          title: "Extract Values",
          version: "1.5",
          description: "Extract single and multiple values",
          code: `// Extract single value after validation
String userId = given().
    body(newUser).
when().
    post("/users").
then().
    statusCode(201).
    extract().path("id");

// Extract multiple values
ExtractableResponse<Response> response = get("/user/123")
    .then()
    .statusCode(200)
    .extract();

String name = response.path("name");
String email = response.path("email");
int age = response.path("age");

// Extract entire response
Response fullResponse = response.response();

// Extract with type
Integer userId = get("/user/123").path("id");
List<String> tags = get("/post/1").path("tags");
Map<String, Object> address = get("/user/123").path("address");

// Direct extraction without validation
String name = get("/user/123").path("name");

// Using jsonPath() for complex extraction
JsonPath json = get("/store").jsonPath();
String author = json.getString("store.book[0].author");
List<String> allAuthors = json.getList("store.book.author");
float maxPrice = json.getFloat("store.book.price.max()");`,
          explanation: "Use extract() after validation or path() for direct extraction. JsonPath provides complex queries with closures and aggregations."
        },
        {
          id: "extract-headers-cookies",
          title: "Extract Headers & Cookies",
          version: "1.0",
          description: "Extract metadata from responses",
          code: `// Extract single header
String contentType = get("/api")
    .then()
    .extract()
    .header("Content-Type");

// Extract all headers
Headers allHeaders = get("/api")
    .then()
    .extract()
    .headers();

for (Header header : allHeaders) {
    System.out.println(header.getName() + ": " + header.getValue());
}

// Get header by name
String auth = allHeaders.getValue("Authorization");
List<String> cacheHeaders = allHeaders.getValues("Cache-Control");

// Extract cookies
String sessionId = get("/login")
    .then()
    .extract()
    .cookie("JSESSIONID");

// Extract all cookies
Map<String, String> cookies = get("/login")
    .then()
    .extract()
    .cookies();

// Detailed cookie
Cookie detailedCookie = get("/login")
    .then()
    .extract()
    .detailedCookie("session");

String path = detailedCookie.getPath();
boolean httpOnly = detailedCookie.isHttpOnly();

// Extract status
int statusCode = get("/api").statusCode();
String statusLine = get("/api").statusLine();`,
          explanation: "Headers and cookies can be extracted individually or as collections. Detailed cookies include path, domain, and security attributes."
        },
        {
          id: "extract-body",
          title: "Extract Response Body",
          version: "1.0",
          description: "Get response body in various formats",
          code: `// As String
String body = get("/api").asString();

// As byte array (for binary)
byte[] bytes = get("/download").asByteArray();

// As InputStream (memory efficient)
InputStream stream = get("/large-file").asInputStream();

// Pretty printed
String pretty = get("/api").asPrettyString();

// As specific type (deserialization)
User user = get("/user/123").as(User.class);

// As generic type
List<User> users = get("/users")
    .as(new TypeRef<List<User>>() {});

// From extract()
String body = get("/api")
    .then()
    .statusCode(200)
    .extract()
    .body()
    .asString();

// Peek for debugging (prints and returns)
String body = get("/api")
    .then()
    .statusCode(200)
    .extract()
    .body()
    .asPrettyString();

// Print without capturing
get("/api").prettyPrint();  // Returns String too
get("/api").print();  // Raw print`,
          explanation: "Response body can be extracted as String, bytes, stream, or deserialized objects. Use prettyPrint() for debugging."
        },
        {
          id: "extract-response-time",
          title: "Extract Response Time",
          version: "2.5",
          description: "Measure and extract response timing",
          code: `// Get response time in milliseconds
long timeMs = get("/api").time();

// Get time in specific unit
long timeSeconds = get("/api").timeIn(TimeUnit.SECONDS);
long timeMicros = get("/api").timeIn(TimeUnit.MICROSECONDS);

// Validate response time
get("/api")
.then()
    .time(lessThan(2000L));  // Less than 2 seconds

// Response time in specific unit
get("/api")
.then()
    .time(lessThan(500L), TimeUnit.MILLISECONDS);

// Extract after validation
long responseTime = get("/api")
    .then()
    .statusCode(200)
    .extract()
    .time();

// Performance assertions
get("/api")
.then()
    .time(allOf(
        greaterThanOrEqualTo(100L),
        lessThan(1000L)
    ));

// Log response time
Response response = get("/api");
System.out.println("Response time: " + response.time() + "ms");

// Store for reporting
Map<String, Long> performanceMetrics = new HashMap<>();
performanceMetrics.put("getUsers", get("/users").time());
performanceMetrics.put("getOrders", get("/orders").time());`,
          explanation: "Response time measurement helps performance testing. Use time() for extraction and time(Matcher) for validation."
        }
      ]
    },
    {
      id: "syntactic-sugar",
      title: "Syntactic Sugar",
      icon: Zap,
      description: "Convenience methods and shortcuts",
      items: [
        {
          id: "with-and-given",
          title: "with() and given()",
          version: "1.0",
          description: "Equivalent methods for readability",
          code: `// given() and with() are equivalent
given().
    param("name", "John").
when().
    get("/greeting");

with().
    param("name", "John").
when().
    get("/greeting");

// They can be used interchangeably
with().
    header("Accept", "application/json").
given().
    param("id", 123).
when().
    get("/user");`,
          explanation: "given() and with() are synonyms for readability. Choose based on what reads better in your test context."
        },
        {
          id: "and-method",
          title: "and() Method",
          version: "1.0",
          description: "Chain methods for readability",
          code: `// Use and() for readability
given().
    param("firstName", "John").
    and().
    param("lastName", "Doe").
when().
    get("/greeting");

// In response validation
get("/user/123")
.then().
    statusCode(200).
    and().
    body("name", equalTo("John")).
    and().
    body("email", containsString("@"));

// Chain configuration
given().
    config(config()).
    and().
    header("Accept", "application/json").
    and().
    cookie("session", "abc").
when().
    get("/api");

// and() is purely syntactic - these are equivalent:
given().param("a", 1).param("b", 2). ...
given().param("a", 1).and().param("b", 2). ...`,
          explanation: "and() is syntactic sugar that returns the same object. It improves readability without changing behavior."
        },
        {
          id: "expect-assert",
          title: "expect() vs assertThat()",
          version: "1.0",
          description: "Alternative assertion styles",
          code: `// then() style (recommended)
get("/api")
.then()
    .statusCode(200)
    .body("status", equalTo("ok"));

// expect() style (legacy but supported)
expect().
    statusCode(200).
    body("status", equalTo("ok")).
when().
    get("/api");

// assertThat() for explicit assertion
get("/api")
.then()
    .assertThat()
    .statusCode(200)
    .body("status", equalTo("ok"));

// Mix styles if needed
given().
    param("x", "y").
expect().
    statusCode(200).
when().
    get("/api");

// Using static imports for cleaner code
import static io.restassured.RestAssured.*;
import static org.hamcrest.Matchers.*;

// This enables:
get("/api").then().statusCode(200);
// Instead of:
RestAssured.get("/api").then().statusCode(200);`,
          explanation: "then() is the modern style, expect() is legacy BDD style. assertThat() is explicit assertion. All work equivalently."
        },
        {
          id: "shortcuts",
          title: "Method Shortcuts",
          version: "1.0",
          description: "Quick methods for common operations",
          code: `// Direct HTTP method calls (without given/when)
get("/users");
post("/users");
put("/users/1");
delete("/users/1");
patch("/users/1");
head("/users");
options("/users");

// With path parameters inline
get("/users/{id}", 123);
get("/users/{id}/orders/{orderId}", 123, 456);

// Request with body shortcut
post("/users", "{\\"name\\": \\"John\\"}");

// Immediate assertions
get("/users").then().statusCode(200);

// Immediate extraction
String name = get("/users/1").path("name");

// Chained in single line
get("/users").then().statusCode(200).body("$", hasSize(greaterThan(0)));

// Using Response directly
Response response = get("/users");
assertEquals(200, response.statusCode());

// Content shortcuts
get("/users").asString();  // Body as String
get("/users").as(User[].class);  // Deserialize array
get("/users").jsonPath();  // Get JsonPath directly`,
          explanation: "Shortcuts reduce boilerplate for simple tests. Use full given/when/then for complex scenarios requiring configuration."
        }
      ]
    },
    {
      id: "groovy-closures",
      title: "Groovy Closures",
      icon: Code,
      description: "Advanced filtering and transformation",
      items: [
        {
          id: "find-findall",
          title: "find() and findAll()",
          version: "1.0",
          description: "Filter collections with closures",
          code: `// find() returns first match
get("/store")
.then()
    .body("store.book.find { it.price < 10 }.title", 
          equalTo("Sayings of the Century"));

// findAll() returns all matches
get("/store")
.then()
    .body("store.book.findAll { it.price < 10 }.title", 
          hasItems("Sayings of the Century", "Moby Dick"));

// Complex conditions
get("/users")
.then()
    .body("users.findAll { it.age > 18 && it.status == 'active' }.size()", 
          greaterThan(0));

// Nested find
get("/orders")
.then()
    .body("orders.find { it.status == 'pending' }.items.find { it.quantity > 5 }.name",
          notNullValue());

// findAll with multiple conditions
get("/products")
.then()
    .body("products.findAll { it.price > 10 && it.inStock == true && it.category == 'electronics' }",
          hasSize(greaterThan(0)));

// Extract filtered data
List<String> expensiveBooks = get("/store").path(
    "store.book.findAll { it.price > 15 }.title");`,
          explanation: "find() returns the first matching element, findAll() returns all matches. The closure variable 'it' represents each element."
        },
        {
          id: "collect-sum",
          title: "collect(), sum(), max(), min()",
          version: "1.0",
          description: "Transform and aggregate data",
          code: `// collect() transforms each element
get("/store")
.then()
    .body("store.book.title.collect { it.toUpperCase() }", 
          hasItem("MOBY DICK"));

// collect() to get lengths
get("/store")
.then()
    .body("store.book.author.collect { it.length() }", 
          everyItem(greaterThan(0)));

// sum() aggregates numeric values
get("/store")
.then()
    .body("store.book.price.sum()", greaterThan(30.0f));

// max() gets maximum value
get("/store")
.then()
    .body("store.book.price.max()", equalTo(22.99f));

// min() gets minimum value  
get("/store")
.then()
    .body("store.book.price.min()", equalTo(8.95f));

// Combine operations
get("/orders")
.then()
    .body("orders.items.flatten().price.sum()", greaterThan(100.0f));

// Extract aggregated values
float totalPrice = get("/store").path("store.book.price.sum()");
float avgPrice = get("/store").path("store.book.price.sum() / store.book.size()");`,
          explanation: "collect() maps elements to new values. sum(), max(), min() aggregate numeric collections. Chain operations for complex calculations."
        },
        {
          id: "spread-operator",
          title: "Spread Operator (*)",
          version: "1.0",
          description: "Call methods on all collection elements",
          code: `// Spread operator calls method on each element
get("/store")
.then()
    .body("store.book.author*.length()", everyItem(greaterThan(3)));

// Equivalent to collect
// author*.length() == author.collect { it.length() }

// Chain spread operations
get("/users")
.then()
    .body("users*.name*.toUpperCase()", hasItem("JOHN"));

// Sum with spread
get("/store")
.then()
    .body("store.book.author*.length().sum()", greaterThan(50));

// Spread on nested properties
get("/orders")
.then()
    .body("orders*.customer*.email", everyItem(containsString("@")));

// Spread with method parameters
get("/products")
.then()
    .body("products*.name*.substring(0, 3)", hasItem("Wid"));

// Extract with spread
List<Integer> lengths = get("/store").path("store.book.author*.length()");`,
          explanation: "The spread operator (*) calls a method on every element in a collection. It's shorthand for collect() with a method call."
        },
        {
          id: "depth-first",
          title: "Depth-First Search (**)",
          version: "1.0",
          description: "Search entire document tree",
          code: `// ** searches entire document
get("/shopping")
.then()
    .body("**.find { it.@type == 'groceries' }", hasItems("Chocolate", "Coffee"));

// Find any element with property
get("/complex-data")
.then()
    .body("**.find { it.name == 'target' }.value", equalTo("found"));

// Find all matches anywhere
get("/data")
.then()
    .body("**.findAll { it.status == 'active' }.size()", greaterThan(0));

// Depth-first with attribute search
get("/xml-doc")
.then()
    .body("**.findAll { it.@id != null }*.@id", hasItems("1", "2", "3"));

// Search for specific structure
get("/nested")
.then()
    .body("**.find { it.type == 'config' && it.enabled == true }.name",
          equalTo("MyConfig"));

// Extract from anywhere in document
String targetValue = get("/deep-nested").path(
    "**.find { it.key == 'secretKey' }.value");

// Count occurrences
int activeCount = get("/data").path(
    "**.findAll { it.active == true }.size()");`,
          explanation: "The ** operator performs depth-first search through the entire document tree. Useful for finding elements regardless of their location."
        }
      ]
    },
    {
      id: "parsers-config",
      title: "Parsers & Content Types",
      icon: Settings,
      description: "Configure response parsing",
      items: [
        {
          id: "custom-parsers",
          title: "Custom Parsers",
          version: "1.2",
          description: "Register parsers for custom content types",
          code: `// Register parser for custom content type
RestAssured.registerParser("application/vnd.myapp+json", Parser.JSON);
RestAssured.registerParser("application/vnd.myapp+xml", Parser.XML);

// Now these content types parse correctly
get("/custom-json")
.then()
    .body("data.id", equalTo(123));

// Unregister when done
RestAssured.unregisterParser("application/vnd.myapp+json");

// Per-request parser
get("/custom")
.then()
    .using().parser("application/custom", Parser.JSON)
    .body("value", equalTo("test"));

// Available parsers
Parser.JSON    // application/json
Parser.XML     // application/xml  
Parser.HTML    // text/html
Parser.TEXT    // text/plain

// In response specification
ResponseSpecification spec = new ResponseSpecBuilder()
    .registerParser("application/hal+json", Parser.JSON)
    .build();`,
          explanation: "Custom parsers handle non-standard content types. Register globally with RestAssured or per-request with using().parser()."
        },
        {
          id: "default-parser",
          title: "Default Parser",
          version: "1.2",
          description: "Handle responses without content-type",
          code: `// Set default parser globally
RestAssured.defaultParser = Parser.JSON;

// Now responses without content-type parse as JSON
get("/no-content-type")
.then()
    .body("data", notNullValue());

// Per-request default parser
get("/unknown")
.then()
    .using().defaultParser(Parser.XML)
    .body("root.element", equalTo("value"));

// Reset default parser
RestAssured.defaultParser = null;  // Back to default behavior

// In response specification
ResponseSpecification spec = new ResponseSpecBuilder()
    .setDefaultParser(Parser.JSON)
    .build();

given().
    spec(requestSpec).
when().
    get("/api").
then().
    spec(spec).
    body("status", equalTo("ok"));`,
          explanation: "Default parser handles responses that don't specify content-type. Useful for legacy APIs or misconfigured servers."
        },
        {
          id: "content-type-mapping",
          title: "Content Type Mapping",
          version: "1.4",
          description: "Configure serialization by content type",
          code: `// Configure charset for specific content type
RestAssured.config = config().encoderConfig(
    encoderConfig().defaultCharsetForContentType("UTF-16", "application/xml"));

// Configure decoder charset
RestAssured.config = config().decoderConfig(
    decoderConfig().defaultCharsetForContentType("UTF-16", "application/xml"));

// JSON always uses UTF-8 by default (RFC 4627)
// Override if needed:
RestAssured.config = config().encoderConfig(
    encoderConfig().defaultCharsetForContentType("ISO-8859-1", "application/json"));

// Multiple content type configurations
RestAssured.config = config()
    .encoderConfig(encoderConfig()
        .defaultCharsetForContentType("UTF-8", "application/json")
        .defaultCharsetForContentType("UTF-16", "application/xml")
        .defaultCharsetForContentType("ISO-8859-1", "text/plain"));

// Configure object mapper for content type
RestAssured.config = config().objectMapperConfig(
    objectMapperConfig()
        .defaultObjectMapperType(ObjectMapperType.JACKSON_2));`,
          explanation: "Configure how different content types are encoded and decoded. JSON uses UTF-8 by default per RFC 4627."
        },
        {
          id: "big-decimal-config",
          title: "Number Return Type",
          version: "2.0",
          description: "Configure JSON number parsing",
          code: `// Return JSON numbers as BigDecimal
RestAssured.config = config().jsonConfig(
    jsonConfig().numberReturnType(NumberReturnType.BIG_DECIMAL));

// Now validate with BigDecimal
get("/price")
.then()
    .body("price", equalTo(new BigDecimal("12.99")));

// Return as Double (default for decimals)
RestAssured.config = config().jsonConfig(
    jsonConfig().numberReturnType(NumberReturnType.DOUBLE));

get("/price")
.then()
    .body("price", equalTo(12.99d));

// Return as Float
RestAssured.config = config().jsonConfig(
    jsonConfig().numberReturnType(NumberReturnType.FLOAT_AND_DOUBLE));

get("/price")
.then()
    .body("price", equalTo(12.99f));

// Per-request configuration
given().
    config(config().jsonConfig(
        jsonConfig().numberReturnType(BIG_DECIMAL))).
when().
    get("/financial").
then().
    body("amount", equalTo(new BigDecimal("1000000.99")));

// Extract as BigDecimal
BigDecimal price = given().
    config(config().jsonConfig(jsonConfig().numberReturnType(BIG_DECIMAL))).
when().
    get("/price").
    path("price");`,
          explanation: "Configure number return type for precision-sensitive applications. Use BigDecimal for financial calculations to avoid floating-point errors."
        }
      ]
    },
    {
      id: "connection-handling",
      title: "Connection Handling",
      icon: Globe,
      description: "HTTP client and connection management",
      items: [
        {
          id: "reuse-connections",
          title: "Connection Reuse",
          version: "1.5",
          description: "Manage HTTP client connections",
          code: `// Reuse HTTP client instance (connection pooling)
RestAssured.config = config().httpClient(
    httpClientConfig().reuseHttpClientInstance());

// Now all requests share the same HTTP client
for (int i = 0; i < 100; i++) {
    get("/api/" + i).then().statusCode(200);
}

// Close idle connections after each response
RestAssured.config = config().connectionConfig(
    connectionConfig().closeIdleConnectionsAfterEachResponse());

// Custom HTTP client factory
RestAssured.config = config().httpClient(
    httpClientConfig().httpClientFactory(
        new HttpClientConfig.HttpClientFactory() {
            @Override
            public HttpClient createHttpClient() {
                return HttpClients.custom()
                    .setMaxConnTotal(100)
                    .setMaxConnPerRoute(20)
                    .build();
            }
        }));

// Configure connection timeouts
RestAssured.config = config().httpClient(
    httpClientConfig()
        .setParam("http.connection.timeout", 5000)
        .setParam("http.socket.timeout", 10000));`,
          explanation: "Connection reuse improves performance for multiple requests. Configure pooling for high-volume testing scenarios."
        },
        {
          id: "follow-redirects",
          title: "Redirect Handling",
          version: "1.5",
          description: "Configure redirect behavior",
          code: `// Disable redirects
given().
    redirects().follow(false).
when().
    get("/redirect").
then().
    statusCode(302).
    header("Location", containsString("/destination"));

// Limit max redirects
given().
    redirects().max(5).
when().
    get("/chain-redirect");

// DSL style
given().
    redirects().follow(true).and().
    redirects().max(3).
when().
    get("/redirect");

// Global configuration
RestAssured.config = config().redirect(
    redirectConfig()
        .followRedirects(false)
        .maxRedirects(10));

// Allow circular redirects
RestAssured.config = config().redirect(
    redirectConfig()
        .followRedirects(true)
        .allowCircularRedirects(true));

// Reject relative redirects
RestAssured.config = config().redirect(
    redirectConfig().rejectRelativeRedirect(true));

// Capture redirect chain
Response response = given().
    redirects().follow(false).
when().
    get("/redirect");

String location = response.header("Location");
// Follow manually if needed
get(location).then().statusCode(200);`,
          explanation: "Redirect configuration controls automatic following of 3xx responses. Disable to test redirect behavior explicitly."
        },
        {
          id: "keep-alive",
          title: "Keep-Alive & Timeouts",
          version: "2.0",
          description: "Configure connection persistence",
          code: `// Set socket timeout
RestAssured.config = config().httpClient(
    httpClientConfig()
        .setParam("http.socket.timeout", 30000));  // 30 seconds

// Set connection timeout
RestAssured.config = config().httpClient(
    httpClientConfig()
        .setParam("http.connection.timeout", 10000));  // 10 seconds

// Both timeouts
RestAssured.config = config().httpClient(
    httpClientConfig()
        .setParam("http.socket.timeout", 30000)
        .setParam("http.connection.timeout", 10000));

// Per-request timeout
given().
    config(config().httpClient(
        httpClientConfig()
            .setParam("http.socket.timeout", 5000))).
when().
    get("/slow-endpoint");

// Connection manager timeout
RestAssured.config = config().httpClient(
    httpClientConfig()
        .setParam("http.conn-manager.timeout", 5000));

// Test timeout behavior
@Test(timeout = 5000)  // JUnit timeout
void shouldTimeoutSlowRequest() {
    given().
        config(config().httpClient(
            httpClientConfig()
                .setParam("http.socket.timeout", 1000))).
    when().
        get("/very-slow").
    then().
        // Will throw SocketTimeoutException
        statusCode(anyOf(is(200), is(504)));
}`,
          explanation: "Timeout configuration prevents tests from hanging on slow responses. Set appropriate timeouts for your API's expected response times."
        }
      ]
    }
  ]
};

// Code block component with copy functionality
const CodeBlock = ({ code, language = "java" }) => {
  const [copied, setCopied] = useState(false);
  const codeRef = useRef(null);

  useEffect(() => {
    if (codeRef.current) {
      Prism.highlightElement(codeRef.current);
    }
  }, [code]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Detect language from code content
  const detectLanguage = () => {
    if (code.includes('fun ') || code.includes('val ') || code.includes('var ')) return 'kotlin';
    if (code.includes('def ') && code.includes('object ')) return 'scala';
    if (code.includes('<dependency>') || code.includes('<?xml')) return 'xml';
    if (code.startsWith('{') || code.startsWith('[')) return 'json';
    if (code.includes('closures') || code.includes('findAll {')) return 'groovy';
    return 'java';
  };

  const langClass = `language-${detectLanguage()}`;

  return (
    <div className="code-block-wrapper">
      <button 
        className="copy-button"
        onClick={copyToClipboard}
        data-testid="copy-code-btn"
      >
        {copied ? <Check size={16} /> : <Copy size={16} />}
        {copied ? "Copied!" : "Copy"}
      </button>
      <pre className="code-block">
        <code ref={codeRef} className={langClass}>{code}</code>
      </pre>
    </div>
  );
};

// Single documentation item component
const DocItem = ({ item, isExpanded, onToggle, showVersionBadge = true, isBookmarked, onToggleBookmark }) => {
  const versionColor = item.version ? getVersionColor(item.version) : "#6a6a7a";
  
  const handleBookmarkClick = (e) => {
    e.stopPropagation();
    onToggleBookmark(item.id);
  };
  
  return (
    <div className={`doc-item ${isExpanded ? 'expanded' : ''} ${isBookmarked ? 'bookmarked' : ''}`} data-testid={`doc-item-${item.id}`}>
      <div 
        className="doc-item-header"
        onClick={onToggle}
        data-testid={`toggle-${item.id}`}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onToggle(); }}
      >
        <div className="doc-item-title">
          {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          <h4>{item.title}</h4>
          {showVersionBadge && item.version && parseFloat(item.version) > 1.0 && (
            <span 
              className="version-badge" 
              style={{ backgroundColor: versionColor + '20', color: versionColor, borderColor: versionColor }}
              data-testid={`version-badge-${item.id}`}
            >
              v{item.version}+
            </span>
          )}
          <button
            className={`bookmark-btn ${isBookmarked ? 'active' : ''}`}
            onClick={handleBookmarkClick}
            data-testid={`bookmark-${item.id}`}
            title={isBookmarked ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Heart size={16} fill={isBookmarked ? '#f5c518' : 'none'} />
          </button>
        </div>
        <span className="doc-item-description">{item.description}</span>
      </div>
      
      {isExpanded && (
        <div className="doc-item-content">
          <CodeBlock code={item.code} />
          <div className="explanation">
            <strong>Explanation:</strong> {item.explanation}
          </div>
        </div>
      )}
    </div>
  );
};

// Category section component
const CategorySection = ({ category, expandedItems, toggleItem, selectedVersion, bookmarks, onToggleBookmark }) => {
  const IconComponent = category.icon;
  
  // Filter items based on selected version
  const filteredItems = category.items.filter(item => 
    isAvailableInVersion(item.version || "1.0", selectedVersion)
  );
  
  // Don't render category if no items match
  if (filteredItems.length === 0) return null;
  
  return (
    <section className="category-section" id={category.id} data-testid={`category-${category.id}`}>
      <div className="category-header">
        <div className="category-icon">
          <IconComponent size={24} />
        </div>
        <div className="category-info">
          <h2>{category.title}</h2>
          <p>{category.description}</p>
        </div>
        <span className="category-count">{filteredItems.length} items</span>
      </div>
      
      <div className="category-items">
        {filteredItems.map(item => (
          <DocItem 
            key={item.id}
            item={item}
            isExpanded={expandedItems.has(item.id)}
            onToggle={() => toggleItem(item.id)}
            showVersionBadge={selectedVersion === "all"}
            isBookmarked={bookmarks.has(item.id)}
            onToggleBookmark={onToggleBookmark}
          />
        ))}
      </div>
    </section>
  );
};

// Sidebar navigation
const Sidebar = ({ categories, activeCategory, isOpen, onClose }) => {
  return (
    <>
      {isOpen && <div className="sidebar-overlay" onClick={onClose} />}
      <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h3>Navigation</h3>
          <button className="close-sidebar" onClick={onClose} data-testid="close-sidebar">
            <X size={20} />
          </button>
        </div>
        <nav className="sidebar-nav">
          {categories.map(cat => {
            const IconComponent = cat.icon;
            return (
              <a 
                key={cat.id}
                href={`#${cat.id}`}
                className={`nav-item ${activeCategory === cat.id ? 'active' : ''}`}
                onClick={onClose}
                data-testid={`nav-${cat.id}`}
              >
                <IconComponent size={18} />
                <span>{cat.title}</span>
              </a>
            );
          })}
        </nav>
      </aside>
    </>
  );
};

// Main App component
function App() {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [activeCategory, setActiveCategory] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandAll, setExpandAll] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState("all");
  const [versionDropdownOpen, setVersionDropdownOpen] = useState(false);
  const [bookmarks, setBookmarks] = useState(() => {
    const saved = localStorage.getItem(BOOKMARKS_STORAGE_KEY);
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const mainRef = useRef(null);
  const searchInputRef = useRef(null);

  // Save bookmarks to localStorage
  useEffect(() => {
    localStorage.setItem(BOOKMARKS_STORAGE_KEY, JSON.stringify([...bookmarks]));
  }, [bookmarks]);

  // Toggle bookmark
  const toggleBookmark = useCallback((itemId) => {
    setBookmarks(prev => {
      const newBookmarks = new Set(prev);
      if (newBookmarks.has(itemId)) {
        newBookmarks.delete(itemId);
      } else {
        newBookmarks.add(itemId);
      }
      return newBookmarks;
    });
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+K or Cmd+K - Focus search
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      // Escape - Clear search or close sidebar
      if (e.key === 'Escape') {
        if (searchQuery) {
          setSearchQuery("");
        } else if (sidebarOpen) {
          setSidebarOpen(false);
        } else if (showShortcuts) {
          setShowShortcuts(false);
        }
      }
      // Ctrl+E - Expand/Collapse all
      if ((e.ctrlKey || e.metaKey) && e.key === 'e') {
        e.preventDefault();
        toggleExpandAll();
      }
      // Ctrl+B - Toggle favorites filter
      if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
        e.preventDefault();
        setShowFavoritesOnly(prev => !prev);
      }
      // ? - Show shortcuts help
      if (e.key === '?' && !e.target.matches('input, textarea')) {
        e.preventDefault();
        setShowShortcuts(prev => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchQuery, sidebarOpen, showShortcuts]);

  // Filter categories and items based on search, version, AND favorites
  const filteredCategories = restAssuredData.categories.map(category => ({
    ...category,
    items: category.items.filter(item => {
      const matchesSearch = 
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.explanation.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesVersion = isAvailableInVersion(item.version || "1.0", selectedVersion);
      const matchesFavorites = !showFavoritesOnly || bookmarks.has(item.id);
      return matchesSearch && matchesVersion && matchesFavorites;
    })
  })).filter(category => category.items.length > 0);

  // Toggle item expansion
  const toggleItem = (itemId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  // Expand/Collapse all
  const toggleExpandAll = () => {
    if (expandAll) {
      setExpandedItems(new Set());
    } else {
      const allItemIds = new Set();
      filteredCategories.forEach(cat => {
        cat.items.forEach(item => allItemIds.add(item.id));
      });
      setExpandedItems(allItemIds);
    }
    setExpandAll(!expandAll);
  };

  // Set document title
  useEffect(() => {
    document.title = "Testing Professor | REST Assured Fundamentals";
  }, []);

  // Track active section on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveCategory(entry.target.id);
          }
        });
      },
      { rootMargin: '-20% 0px -80% 0px' }
    );

    document.querySelectorAll('.category-section').forEach(section => {
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, [filteredCategories]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.version-filter')) {
        setVersionDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const totalFunctions = restAssuredData.categories.reduce(
    (sum, cat) => sum + cat.items.length, 0
  );

  const filteredFunctionCount = filteredCategories.reduce(
    (sum, cat) => sum + cat.items.length, 0
  );

  const selectedVersionData = VERSIONS.find(v => v.value === selectedVersion);

  return (
    <div className="app-container" data-testid="rest-assured-docs">
      {/* Header */}
      <header className="main-header">
        <div className="header-left">
          <button 
            className="menu-toggle"
            onClick={() => setSidebarOpen(true)}
            data-testid="menu-toggle"
          >
            <Menu size={24} />
          </button>
          <div className="logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_api-test-samples/artifacts/xj0gmogs_Testing_Professor.png" 
              alt="Testing Professor" 
              className="logo-image"
            />
            <div className="logo-text">
              <h1>Testing <span>Professor</span></h1>
              <span className="subtitle">REST Assured Fundamentals</span>
            </div>
          </div>
        </div>
        
        <div className="header-center">
          <div className="search-container">
            <Search size={20} />
            <input 
              type="text"
              placeholder="Search functions, methods, examples..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-input"
              ref={searchInputRef}
            />
            <span className="keyboard-hint">⌘K</span>
            {searchQuery && (
              <button 
                className="clear-search"
                onClick={() => setSearchQuery("")}
                data-testid="clear-search"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        <div className="header-right">
          {/* Favorites Filter */}
          <button
            className={`favorites-filter-btn ${showFavoritesOnly ? 'active' : ''}`}
            onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
            data-testid="favorites-filter-btn"
            title={showFavoritesOnly ? 'Show all functions' : 'Show favorites only'}
          >
            <Heart size={18} fill={showFavoritesOnly ? '#f5c518' : 'none'} />
            {bookmarks.size > 0 && <span className="favorites-count">{bookmarks.size}</span>}
          </button>

          {/* Keyboard Shortcuts Help */}
          <button
            className="shortcuts-btn"
            onClick={() => setShowShortcuts(!showShortcuts)}
            data-testid="shortcuts-btn"
            title="Keyboard shortcuts (?)"
          >
            <Keyboard size={18} />
          </button>

          {/* Version Filter Dropdown */}
          <div className="version-filter" data-testid="version-filter">
            <button 
              className="version-filter-btn"
              onClick={() => setVersionDropdownOpen(!versionDropdownOpen)}
              data-testid="version-filter-btn"
              style={{ borderColor: selectedVersionData?.color }}
            >
              <Filter size={16} />
              <span>{selectedVersionData?.label || "All Versions"}</span>
              <ChevronDown size={16} className={versionDropdownOpen ? 'rotated' : ''} />
            </button>
            {versionDropdownOpen && (
              <div className="version-dropdown" data-testid="version-dropdown">
                {VERSIONS.map(version => (
                  <button
                    key={version.value}
                    className={`version-option ${selectedVersion === version.value ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedVersion(version.value);
                      setVersionDropdownOpen(false);
                    }}
                    data-testid={`version-option-${version.value}`}
                    style={{ '--version-color': version.color }}
                  >
                    <span className="version-dot" style={{ backgroundColor: version.color }}></span>
                    {version.label}
                    {selectedVersion === version.value && <Check size={14} />}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <button 
            className="expand-toggle"
            onClick={toggleExpandAll}
            data-testid="expand-all-btn"
          >
            {expandAll ? "Collapse All" : "Expand All"}
          </button>
          <a 
            href="https://github.com/rest-assured/rest-assured" 
            target="_blank" 
            rel="noopener noreferrer"
            className="github-link"
            data-testid="github-link"
          >
            <ExternalLink size={18} />
            GitHub
          </a>
        </div>
      </header>

      {/* Sidebar */}
      <Sidebar 
        categories={filteredCategories}
        activeCategory={activeCategory}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Main Content */}
      <main className="main-content" ref={mainRef}>
        {/* Hero Section */}
        <div className="hero-section">
          <div className="hero-content">
            <div className="hero-brand">
              <img 
                src="https://customer-assets.emergentagent.com/job_api-test-samples/artifacts/xj0gmogs_Testing_Professor.png" 
                alt="Testing Professor" 
                className="hero-logo"
              />
              <div className="hero-titles">
                <h1>REST Assured <span>Fundamentals</span></h1>
                <p className="hero-tagline">by Testing Professor</p>
              </div>
            </div>
            <p className="hero-description">
              Master REST Assured - the Java DSL for testing REST APIs.
              {selectedVersion === "all" 
                ? ` All ${totalFunctions}+ functions with examples in one place.`
                : ` Showing ${filteredFunctionCount} functions available in version ${selectedVersionData?.label}.`
              }
            </p>
            <div className="hero-stats">
              <div className="stat">
                <span className="stat-value">{filteredCategories.length}</span>
                <span className="stat-label">Categories</span>
              </div>
              <div className="stat">
                <span className="stat-value">{filteredFunctionCount}</span>
                <span className="stat-label">Functions</span>
              </div>
              <div className="stat">
                <span className="stat-value">v6.0.0</span>
                <span className="stat-label">Latest Version</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Start */}
        <div className="quick-start">
          <h2>Quick Start</h2>
          <CodeBlock code={`// Add to pom.xml
<dependency>
    <groupId>io.rest-assured</groupId>
    <artifactId>rest-assured</artifactId>
    <version>6.0.0</version>
    <scope>test</scope>
</dependency>

// Your first test
import static io.restassured.RestAssured.*;
import static org.hamcrest.Matchers.*;

@Test
public void verifyUserEndpoint() {
    given()
        .header("Accept", "application/json")
    .when()
        .get("/users/1")
    .then()
        .statusCode(200)
        .body("name", equalTo("John"))
        .body("email", containsString("@"));
}`} />
        </div>

        {/* Documentation Sections */}
        {filteredCategories.length > 0 ? (
          filteredCategories.map(category => (
            <CategorySection 
              key={category.id}
              category={category}
              expandedItems={expandedItems}
              toggleItem={toggleItem}
              selectedVersion={selectedVersion}
              bookmarks={bookmarks}
              onToggleBookmark={toggleBookmark}
            />
          ))
        ) : (
          <div className="no-results">
            <Search size={48} />
            <h3>No results found</h3>
            <p>{showFavoritesOnly ? 'No favorites yet. Click the heart icon to add functions.' : 'Try adjusting your search query or version filter'}</p>
          </div>
        )}
      </main>

      {/* Keyboard Shortcuts Modal */}
      {showShortcuts && (
        <div className="shortcuts-modal-overlay" onClick={() => setShowShortcuts(false)}>
          <div className="shortcuts-modal" onClick={(e) => e.stopPropagation()} data-testid="shortcuts-modal">
            <div className="shortcuts-modal-header">
              <h3>Keyboard Shortcuts</h3>
              <button onClick={() => setShowShortcuts(false)} data-testid="close-shortcuts">
                <X size={20} />
              </button>
            </div>
            <div className="shortcuts-list">
              <div className="shortcut-item">
                <span className="shortcut-keys"><kbd>⌘</kbd><kbd>K</kbd></span>
                <span className="shortcut-desc">Focus search</span>
              </div>
              <div className="shortcut-item">
                <span className="shortcut-keys"><kbd>⌘</kbd><kbd>E</kbd></span>
                <span className="shortcut-desc">Expand/Collapse all</span>
              </div>
              <div className="shortcut-item">
                <span className="shortcut-keys"><kbd>⌘</kbd><kbd>B</kbd></span>
                <span className="shortcut-desc">Toggle favorites filter</span>
              </div>
              <div className="shortcut-item">
                <span className="shortcut-keys"><kbd>Esc</kbd></span>
                <span className="shortcut-desc">Clear search / Close modal</span>
              </div>
              <div className="shortcut-item">
                <span className="shortcut-keys"><kbd>?</kbd></span>
                <span className="shortcut-desc">Show this help</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="main-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <img 
              src="https://customer-assets.emergentagent.com/job_api-test-samples/artifacts/xj0gmogs_Testing_Professor.png" 
              alt="Testing Professor" 
              className="footer-logo"
            />
            <span>Testing Professor</span>
          </div>
          <p>
            REST Assured is developed by <a href="https://twitter.com/johanhaleby" target="_blank" rel="noopener noreferrer">Johan Haleby</a>
          </p>
          <p>
            Documentation compiled by Testing Professor • 
            <a href="https://github.com/rest-assured/rest-assured/wiki/Usage" target="_blank" rel="noopener noreferrer">
              Official Docs
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
